/**
 * Canonical English translation bundle. This file is the source of truth for
 * the shape of all locale bundles: the {@link Messages} type is derived from
 * it, so adding a key here forces every other locale to provide a
 * translation at compile time.
 *
 * Conventions:
 * - Group keys by feature area (`thumbs`, `appointment`, `blocks`, …) so
 *   related strings stay together and lookups stay readable.
 * - Use `{name}`-style placeholders for variable substitution; consumers
 *   pass `vars` to `t()` to fill them in.
 */
export const enMessages = {
  common: {
    languageUpdateFailed: 'Failed to update language',
    unknownPatient: 'Unknown Patient',
    cancel: 'Cancel',
    confirm: 'Confirm',
    somethingWentWrong: 'Something went wrong. Please try again.',
    copyToClipboard: 'Copy to clipboard',
    copyWithText: 'Copy: {text}',
    copied: 'Copied!',
    newSession: 'New Session',
  },
  widgetHeader: {
    settings: 'Settings',
    accountSettings: 'Account Settings',
    customizeInsights: 'Customize Insights',
    helpAndFeedback: 'Help & Feedback',
    openInEluve: 'Open in Eluve',
    hide: 'Minimize',
    testSentry: 'Test Sentry',
    language: 'Language',
  },
  thumbs: {
    signedOut: 'Signed out',
    signIn: 'Sign in',
    welcome: 'Welcome',
    ready: 'Ready to record',
    recording: 'Recording',
    paused: 'Paused',
    writingNote: 'Writing note…',
    noteReady: 'Note ready',
    completed: 'Completed',
    microphoneRequired: 'Microphone required',
    microphoneDisconnected: 'Microphone disconnected',
    recordingTooShort: 'Recording too short',
    notEnoughInfo: 'Not enough information',
    somethingWentWrong: 'Something went wrong',
    summaryFailed: 'Summary failed',
    startRecording: 'Start recording',
    finish: 'Finish',
    pause: 'Pause',
    recordMore: 'Record more',
    sendToEmr: 'Send to EMR',
    sendToEmrAgain: 'Send to EMR again',
    allowAccess: 'Allow access',
    showDetails: 'Maximize',
    microphone: 'Microphone',
    settings: 'Appointment settings',
    seeNotesTab: 'See Notes tab for details',
    canceled: 'Appointment canceled',
  },
  appointment: {
    noSummaryAvailableTitle: 'No summary available yet',
    noSummaryAvailableDescription:
      'Your note will be generated here once the session is completed.',
    insightsTab: 'Insights',
    notesTab: 'Notes',
    transcriptTab: 'Transcript',
    soapNotesTab: 'SOAP Notes',
    noMicrophoneSelected: 'No microphone selected',
    recordingTooShortTitle: 'Recording too short',
    recordingTooShortBody:
      'Need at least 10 seconds of audio to generate a note. Keep recording, then try again.',
    transcript: {
      empty: 'No transcript.',
      viewTranscript: 'View Transcript',
      hideTranscript: 'Hide Transcript',
      sessionStartedAt: 'Session started {date} at {time}',
      sessionResumedAt: 'Session resumed {date} at {time}',
      sessionPausedAt: 'Session paused {date} at {time}',
    },
    transition: {
      title: 'You have an ongoing session for a different appointment',
      description: 'End the current session in order to continue.',
      endSession: 'End Session',
      sessionPausedToast: 'Your session has been paused',
      sessionPausedToastDescription:
        "Return to {patientName}'s appointment to resume or to generate a note.",
    },
    presence: {
      title: 'Are you still here?',
      description:
        'This session is longer than usual. It is going to automatically pause in',
      pauseNow: 'Pause Now',
      continueRecording: 'Continue Recording',
    },
    onboarding: {
      title: 'Ready to record your first appointment on Eluve?',
      subtitle: 'Tell us a bit about yourself to personalize your experience.',
      uiLanguageLabel: 'What language should Eluve be in?',
      uiLanguageTooltip:
        'You can change this any time from the settings menu in the header.',
      uiLanguageAriaLabel: 'UI Language',
      uiLanguageSearchPlaceholder: 'Search languages...',
      specialtyLabel: 'What is your specialty?',
      specialtyTooltip:
        'This helps us tailor note templates and terminology to your specific practice area.',
      specialtyPlaceholder: 'Select specialty...',
      inputLanguageLabel: 'In what language do you speak to your patients?',
      inputLanguageTooltip:
        'This helps improve transcription accuracy by optimizing for your spoken language.',
      inputLanguageAriaLabel: 'Spoken Language',
      outputLanguageLabel: 'In what language do you write your SOAP notes?',
      outputLanguageAriaLabel: 'Note Language',
      save: 'Save',
      saveFailed: 'Failed to save preferences. Please try again.',
    },
    audioFile: {
      unsupportedFormat: 'Unsupported format: .{extension}. Use {formats}.',
      tooLarge: 'File too large ({size}MB). Max 100MB.',
      uploaded: 'Audio file uploaded. Processing will begin shortly.',
      processFailed: 'Failed to process audio file.',
      processing: 'Processing...',
      dropHere: 'Drop audio file to process',
    },
    session: {
      unavailableTitle: 'Session Unavailable',
      unavailableDescription:
        'Your session could not be loaded. Click Retry to restore it.',
      retry: 'Retry',
    },
    languageSelector: {
      regenerateFailed: 'Failed to regenerate summary. Please try again.',
      regeneratingToast: 'Regenerating your summary in the new language',
      regeneratingToastDescription: 'Your new summary should be available soon',
      spokenLanguageWarning:
        "Changing the spoken language will re-transcribe the entire conversation from the session's start.",
      notesLanguageWarning:
        'Changing the notes language will generate a new note',
      notesLanguageWarningDescription:
        'The new note will be generated in {language}.',
      notesLanguageWarningOverwrite: 'This will overwrite the current summary.',
      theSelectedLanguage: 'the selected language',
      spokenLanguageLabel: 'Spoken Language:',
      spokenLanguageAriaLabel: 'Spoken Language',
      notesLanguageLabel: 'Notes Language:',
      notesLanguageAriaLabel: 'Notes Language',
    },
    signedSummary: {
      amendmentFailed: 'Failed to create amendment',
      additionalNotes: 'Additional Notes',
    },
    summarization: {
      noSummaryAvailable: 'No summary available for this appointment',
    },
    summaryFailed: {
      regenerateNote: 'Try again',
    },
    insights: {
      differentialDiagnoses: 'Differential Diagnoses',
      generate: 'Generate',
      generateBlockedTooltip:
        'Pre-Visit Insights are only generated for upcoming or in-progress visits.',
      blockedTitle: 'Pre-Visit Insights unavailable',
      blockedMessage:
        "This appointment is no longer upcoming, so new Pre-Visit Insights aren't generated.",
      title: 'AI Insights',
      tooltip:
        'AI-generated insights based on patient history to help inform your clinical decisions.',
      collapse: 'Collapse',
      show: 'Show',
      loading: {
        reviewingPriorNotes: "Reviewing patient's prior notes…",
        summarizingDocumentation: 'Summarizing documentation…',
        gatheringInsights: 'Gathering insights…',
      },
      pvi: {
        noDataTitle: 'No previous notes for this patient',
        noDataMessage:
          'Eluve has no previous data on this patient. Start recording your appointment to generate your first note for this patient.',
        errorTitle: 'Could not generate AI insights',
        errorMessage:
          "Eluve couldn't generate insights for this patient. You can still record and summarize your appointment.",
      },
    },
    ddx: {
      couldNotGenerate: 'Could not generate DDx',
      couldNotGenerateDescription:
        "Eluve couldn't generate differential diagnoses for this appointment.",
      notGenerated:
        'No diagnoses available yet. Please check back after recording and summarizing your appointment.',
      empty: 'No differential diagnoses were identified.',
      considerations: 'CONSIDERATIONS',
      feedbackPrompt: 'Were these diagnoses helpful?',
      loading: {
        analyzing: 'Analyzing clinical context…',
        identifying: 'Identifying differential diagnoses…',
        evaluating: 'Evaluating diagnostic evidence…',
      },
    },
    feedback: {
      yes: 'Yes',
      no: 'No',
      additionalFeedback: 'Any additional feedback?',
      ratingPrompt:
        'How would you rate your Eluve experience for this session?',
      ratingPoor: 'Poor',
      ratingOkay: 'Okay',
      ratingExcellent: 'Excellent',
      tellMore: 'Tell us more!',
      submit: 'Submit Feedback',
      wasNoteHelpful: 'Was this note helpful?',
      summarySaveFailed: 'Failed to save feedback',
      summaryUpdateFailed: 'Failed to update feedback',
      summaryTagsUpdateFailed: 'Failed to update feedback tags',
      thanksPositive: 'Thanks for the feedback! Glad you liked this one!',
      thanksNegative:
        'Sorry that this summary missed the mark. Every piece of feedback you give (positive or negative) helps us improve our future results.',
      wasHelpful: 'Was this helpful?',
      commentsOptional: 'COMMENTS (OPTIONAL)',
      shareFeedback: 'Share feedback',
      tellMoreOptional: 'Tell us more (optional)',
      submitShort: 'Submit',
      closeForm: 'Close feedback form',
      thumbsUp: 'Thumbs up',
      thumbsDown: 'Thumbs down',
      tagTooLong: 'Too long',
      tagWrongInformation: 'Wrong information',
      tagTooSlow: 'Too slow',
      tagNotEnoughInformation: 'Not enough information',
      tagOtherIssue: 'Other issue',
      tagMissingDetails: 'Missing important details',
      tagIrrelevantDetails: 'Contains irrelevant details',
      tagUnspokenDetails: 'Contains unspoken details',
      tagIncorrectNumber: 'Incorrect number',
      tagIncorrectBodyPart: 'Incorrect body part',
      tagBadFormatting: 'Bad formatting',
      tagTooVerbose: 'Too verbose',
    },
    summary: {
      copiedToClipboard: 'Copied summary to clipboard',
      copySummary: 'Copy Summary',
    },
    syncEhr: {
      syncFailed: 'Sync Failed',
      syncedSuccessfully: 'Synced Successfully!',
      notAvailableLocation: 'Note syncing is not available for your location.',
      notOwner: 'Only the appointment owner may sync this note.',
      chartNotModified:
        "The chart hasn't been modified. Please fill out the chart before syncing the note.",
      selectPatient: 'Select a patient before syncing the note.',
      selectLocation: 'Select a location before syncing the note.',
      notAvailableNow: 'Note syncing is not available right now.',
      pushToEmr: 'Push to EMR',
      pushAgainNotAvailable:
        "This note has already been sent to your EHR and can't be pushed again.",
      syncAgain: 'Sync Again',
    },
    settings: {
      spokenLanguageDescription:
        'Changing the spoken language will re-transcribe the entire conversation, from the start of the session.',
      retranscribeSession: 'Re-transcribe Session',
      notesLanguageDescription:
        'This will regenerate the note in the selected language, and overwrite the current one.',
      regenerateNote: 'Regenerate Note',
      templateDescription:
        'This will regenerate the note using the selected template, and overwrite the current one.',
      filterPlaceholder: 'Type to filter...',
      noLanguagesFound: 'No languages found.',
      noTemplatesFound: 'No templates found.',
      templateUpdateFailed: 'The template could not be updated.',
      spokenLanguageUpdateFailed: 'The spoken language could not be updated.',
      noteLanguageUpdateFailed: 'The note language could not be updated.',
      spokenLanguageItem: 'Spoken language: {language}',
      noteLanguageItem: 'Note language: {language}',
      templateItem: 'Template: {template}',
      notSelected: 'Not selected',
      testSentry: 'Test Sentry',
      spokenLanguageHeading: 'Spoken language',
      noteLanguageHeading: 'Note language',
      templateHeading: 'Template',
      confirmTitle: 'Are you sure?',
    },
    summaryStates: {
      pendingDataTitle: 'Preparing note generation...',
      pendingDataDescription:
        "We are still uploading and processing some files. Once done, we'll generate your note.",
      inProgressTitle: 'Generating note...',
      inProgressDescription:
        "You can leave this page and start a new session! We'll let you know when your note is ready.",
      takingLongerTitle: 'This is taking longer than expected...',
      takingLongerDescription:
        "We're still working on your note. You can leave this page and start a new session! We'll let you know when it is ready.",
      regenerateHint:
        'You can try regenerating the note using a different template.',
    },
    contentStates: {
      notFoundTitle: 'Appointment not found',
      notFoundDescription:
        "We couldn't find this appointment in your EMR. Try opening a different appointment.",
      canceledTitle: 'Appointment canceled',
      canceledDescription:
        'This appointment was canceled in your EMR. Try opening a different appointment.',
      notStartedTitle: 'Ready to record',
      notStartedDescription: 'Press "Start recording" to begin.',
      recordingTitle: 'Recording in progress',
      recordingDescription:
        'Speak naturally. Describe the history, symptoms, and your clinical assessment.',
      pausedTitle: 'Recording paused',
      pausedDescription:
        'Press "Record more" to continue, or "Finish" to write the note.',
      pendingAudioTitle: 'Uploading recording…',
      pendingAudioDescription: 'This will only take a moment.',
      summarizingTitle: 'Writing note…',
      summarizingDescription: 'This usually takes under a minute.',
      takingLongerTitle: 'This is taking longer than usual…',
      takingLongerDescription:
        "We're still writing your note. You can leave this page. We'll let you know when it's ready.",
      stuckTitle: "Your recording didn't go through",
      stuckDescription: 'The upload was interrupted. Try again.',
      degradedTranscriptTitle: "Some audio couldn't be processed",
      degradedTranscriptDescription: 'Try again. This usually fixes it.',
      noteTooShortTitle: 'Recording too short',
      noteTooShortDescription:
        'We only caught a few words from your recording. To write an accurate note, press "Record more" and describe the clinical details.',
      noteNoContextTitle: 'Not enough clinical detail',
      noteNoContextDescription:
        'To write an accurate note, press "Record more" and describe the clinical details. Like symptoms, diagnosis, or plan.',
      failedTitle: 'Something went wrong',
      failedDescription:
        'Our engineers know and are looking into it. Please try again.',
      templateFailedTitle: "This template didn't work",
      templateFailedDescription:
        "We couldn't write your note. Select a different template below, then try again.",
      micPermissionTitle: 'Microphone access needed',
      micPermissionDescription: 'Eluve needs your microphone to record audio.',
      micDisconnectTitle: 'Microphone disconnected',
      micDisconnectDescription:
        'Recording paused. Choose a microphone, then press "Record more".',
      recordingErrorTitle: 'Recording interrupted',
      recordingErrorDescription: 'Try again or refresh the page.',
      sessionExpiredTitle: 'Session expired',
      sessionExpiredDescription:
        'Your recording is saved. Refresh the page to continue.',
      lowStorageTitle: 'Your device is low on storage',
      lowStorageDescription:
        'Free up space on your computer to keep recording. What you recorded is saved.',
      recordingErrorRefresh: 'Refresh',
      recordingErrorRetry: 'Try again',
      recordingErrorDismiss: 'Dismiss',
    },
    summarizationError: {
      somethingWrong: 'Something went wrong',
      failedToGenerate: 'Failed to generate note',
      teamInvestigating:
        'Our team is investigating the issue. You can try again, or email support@eluve.com if you need assistance.',
      recordingTooShort:
        "This recording is too short or doesn't have enough clinical information to generate a note. Resume recording to add more content.",
      templateSelectionRequired:
        'Note generation failed. Select a different template below and click Regenerate to try again.',
    },
    summaryStuck: {
      description:
        "We couldn't generate your note. You can try again, or email support@eluve.com if you need assistance.",
      retryButton: 'Try Again',
      retrySuccessTitle: 'Trying again',
      retrySuccessDescription: 'Your note should be available soon',
      retryErrorTitle: 'Failed to retry',
      retryErrorDescription: 'Please try again or contact support',
    },
    regenerate: {
      button: 'Regenerate',
      title: 'Regenerating your summary',
      overwriteWarning: 'This will overwrite the current summary.',
      templateLabel:
        'Your new note will be generated with the following template:',
      successDescription: 'Your new summary should be available soon',
      errorTitle: 'Failed to regenerate summary',
      errorDescription: 'Please try again',
    },
    disabledTooltips: {
      noMicTitle: 'No Microphone Selected',
      noAudioTitle: 'No Audio Detected',
      noMicDescription: 'Please select one to start recording.',
      noAudioDescription:
        'Please unmute your microphone or select a different one to start recording.',
      storageFullTitle: 'Not enough storage',
      storageFullDescription:
        'Your device is low on storage, which can stop a recording from saving. Free up space and try again.',
      recordingTooShortTitle: 'Recording too short',
      recordingTooShortDescription:
        'We need at least 10 seconds of audio to write a note. Please keep recording.',
    },
    completed: {
      synced: 'Synced',
      syncedWithDate: 'Synced: {date}',
    },
    active: {
      pausedWithDuration: 'Paused - {duration}',
      pausedWithDurationDot: 'Paused · {duration}',
      pauseRecording: 'Pause Recording',
      resumeRecording: 'Resume Recording',
    },
  },
  summaryComparison: {
    triggerTitle: 'ALTERNATIVE OUTPUT',
    triggerDescription: 'View alternative options and select your favorite!',
    thankYouTitle: 'THANK YOU',
    thankYouDescription: 'Your summary has been updated with your selection.',
    selectionFailed: 'Failed to record your selection. Please try again.',
    updateDefaultFailed:
      'Failed to update your default template. Please try again.',
    updateDefaultTitle: 'Update your default template?',
    updateDefaultDescription:
      'The note you selected was generated with a different template than your current default. Would you like to set it as your new default?',
    updateDefaultConfirm: 'Update default',
    dialogTitle: 'Which note do you prefer?',
    dialogDescription:
      'Your choice will help us improve and provide better summaries in the future.',
    preferThisNote: 'I prefer this note',
    generatedIn: 'Generated in {seconds} seconds',
  },
  blocks: {
    transcriptionWarning: {
      title: 'Live transcription has been paused.',
      description:
        'Your recording is still being saved. The transcript will be completed after the appointment.',
    },
    language: {
      searchPlaceholder: 'Search languages...',
    },
    specialty: {
      defaultPlaceholder: 'Select a specialty',
      other: 'Other',
      clearSpecialty: 'Clear specialty',
      enterSpecialty: 'Enter your specialty',
      searchPlaceholder: 'Search specialties...',
    },
    callout: {
      dismissTip: 'Dismiss tip: {title}',
      dontShowAgain: "Don't show this again",
    },
    showAllAppointments: 'Show all appointments',
    dateNavigator: {
      today: 'Today',
    },
    settingsImageUpload: {
      upload: 'Upload',
      uploadAria: 'Upload {label}',
      deleteAria: 'Delete {label}',
    },
  },
  chat: {
    header: {
      patientInsights: 'Patient Insights',
      eluveInsights: 'Eluve Insights',
      newChat: 'New chat',
      pastChats: 'Past chats',
      close: 'Close',
    },
    threads: {
      titlePlaceholder: 'Thread title',
      unnamed: 'Unnamed Conversation',
      conversationAria: 'Conversation: {title}',
      loadMore: 'Load more',
    },
    emptyState: {
      heading: 'Ask Eluve anything',
      more: 'More',
      less: 'Less',
      disclaimer: 'AI can make mistakes. Please double-check responses.',
      seeTermsPrefix: 'See',
      termsLinkLabel: 'T&C',
      seeTermsSuffix: 'for more info.',
    },
    input: {
      placeholder: 'Ask anything',
      attachFile: 'Attach file',
      stop: 'Stop',
      send: 'Send message',
    },
    tools: {
      getPatientNotesActive: 'Searching patient records',
      getPatientNotesCompleted: 'Found patient records',
      createSpecTemplateActive: 'Building template preview',
      createSpecTemplateCompleted: 'Template ready',
      saveSpecTemplateActive: 'Saving template',
      saveSpecTemplateCompleted: 'Template saved',
      draftDocumentActive: 'Drafting document',
      draftDocumentCompleted: 'Document draft ready',
      generateDocumentActive: 'Saving document',
      generateDocumentCompleted: 'Document saved',
      getAppointmentNoteActive: 'Reading appointment note',
      getAppointmentNoteCompleted: 'Appointment note loaded',
      updateAppointmentNoteActive: 'Updating appointment note',
      updateAppointmentNoteCompleted: 'Appointment note updated',
      getCustomInstructionsActive: 'Reading custom instructions',
      getCustomInstructionsCompleted: 'Custom instructions loaded',
      suggestCustomInstructionActive: 'Preparing suggestion',
      suggestCustomInstructionCompleted: 'Suggestion ready',
      saveCustomInstructionActive: 'Saving custom instruction',
      saveCustomInstructionCompleted: 'Custom instruction saved',
      queryFhirResourcesActive: 'Searching clinical records',
      queryFhirResourcesCompleted: 'Found clinical records',
      patientNotesAgentActive: 'Asking Patient Notes Agent',
      patientNotesAgentCompleted: 'Patient Notes Agent responded',
      conversationStarterAgentActive: 'Asking Starter Agent',
      conversationStarterAgentCompleted: 'Starter Agent responded',
      defaultActive: 'Working',
      defaultCompleted: 'Done',
      appointmentNote: 'Appointment note',
    },
    document: {
      drafting: 'Drafting document…',
      saveFailed: 'Failed to save document. Please try again.',
      titlePlaceholder: 'Document title',
      add: 'Add Document',
      copy: 'Copy',
      copied: 'Copied',
      discard: 'Discard',
      addedToNotes: 'Added to notes',
      discarded: 'Discarded',
      discardDialogTitle: 'Discard document?',
      discardDialogDescription:
        'This will discard the drafted document. You can always ask the chat to generate a new one.',
      discardMessage: "Discard the document, I don't need it.",
    },
    starters: {
      preparingSuggestion: 'Preparing suggestion…',
      suggestedTitle: 'Suggested Conversation Starter',
      outputPreference: 'Output Preference (optional)',
      none: 'None',
      dismiss: 'Dismiss',
      dismissMessage: "No thanks, don't save that as a starter.",
      confirmMessage: 'Yes, save that as a conversation starter.',
      confirmWithPreferenceMessage:
        'Yes, save that as a conversation starter with the "{label}" output preference.',
      save: 'Save Starter',
      saving: 'Saving conversation starter…',
      saved: 'Conversation starter saved',
      starterAria: 'Conversation starter: {title}',
    },
    instructions: {
      preparingSuggestion: 'Preparing suggestion…',
      suggestedTitle: 'Suggested Custom Instruction',
      dismiss: 'Dismiss',
      dismissMessage: "No thanks, don't save that as a custom instruction.",
      confirmMessage: 'Yes, save that as a custom instruction.',
      save: 'Save Instruction',
    },
    feedback: {
      wasHelpful: 'Was this response helpful?',
      sendFailed: 'Failed to send feedback',
      updateFailed: 'Failed to update feedback',
      commentFailed: 'Failed to save comment',
      thanksPositive: 'Thanks for the feedback! Glad you liked this response!',
      thanksNegative:
        'Sorry that this response missed the mark. Every piece of feedback helps us improve future results.',
    },
    citations: {
      sources: 'Sources:',
      viewLess: 'View Less',
      viewAll: 'View All ({count})',
    },
    pdf: {
      passwordProtectedTooltip:
        'This PDF is password protected. Click to unlock it!',
      previewTooltip: 'Preview this PDF',
      removeFile: 'Remove {fileName}',
      passwordRequiredTitle: 'Password Required',
      passwordRequired: 'Password is required',
      enterPasswordFor: 'Please enter password to unlock {fileName}.',
      fileFallback: 'file',
      passwordPlaceholder: 'Enter password',
      invalidPassword: 'Invalid password',
      unlockedSuccess: 'PDF unlocked successfully',
      unlocking: 'Unlocking...',
      unlock: 'Unlock',
    },
    knowledgeSources: {
      updateFailed: 'Failed to update knowledge sources',
      saveFileFailed: 'Failed to save uploaded file',
      removeFileFailed: 'Failed to remove file',
      downloadFailed: 'Failed to download file',
      uploadedFiles: 'Uploaded Files',
      uploadFiles: 'Upload files',
      noFiles: 'No files uploaded yet',
    },
    recentNotes: {
      label: 'Recent notes',
      tooltip:
        "Chat may not have access to all notes. It reads this patient's recent notes from your EMR, as well as any notes created in Eluve.",
    },
  },
  errors: {
    homeScreenNotFound: {
      title: 'Home screen unavailable',
      message:
        "We're sorry, but we couldn't load EHR home screen. Please try refreshing the page.",
    },
    notLoggedIn: {
      title: 'Not authorized',
      message: 'Please sign in to your EHR account in a new tab and try again.',
    },
    soapTemplateNotFound: {
      title: 'Template missing',
      message:
        'Template not found: "SOAP Template". Please check if the template exists and try again.',
    },
    noteEditorNotFound: {
      title: 'Note editor unavailable',
      message:
        "We couldn't locate the note editor within EHR. Please ensure it's present and try again.",
    },
    templateNotSupported: {
      title: 'Template not supported',
      message:
        'The selected template is not supported by the EHR. Please select a different template and try again.',
    },
    failedToSign: {
      title: 'Chart entry signature failed',
      message:
        'We encountered an issue while signing the chart entry. Please try again.',
    },
    genericError: {
      title: 'Something went wrong',
      message:
        'Our team is investigating the issue. You can try again, or email support@eluve.com if you need assistance.',
    },
    timeout: {
      title: 'Request timed out',
      message:
        'Your request took too long to process. Please check your internet connection and try again.',
    },
    failedToImport: {
      title: 'Import failed',
      message: 'Failed to import data. Please check the data and try again.',
    },
    staffMismatch: {
      title: 'Appointment owner mismatch',
      message:
        'The appointment owner does not match the current user in EHR. Please ensure you are logged in as the correct user and try again.',
    },
    invalidStatePageRefreshRequired: {
      title: 'Session expired',
      message:
        "We've noticed you're in an inconsistent state. Please refresh the page to continue.",
    },
    micDisconnected: {
      title: 'Microphone not detected',
      message:
        'Your recording has been paused. Please update your microphone selection in order to resume.',
    },
    micPermissionsDenied: {
      title: 'Microphone access required',
      message:
        'Eluve needs permission to access your microphone to record audio.',
    },
    domInjectionFailed: {
      title: "That didn't work",
      message: 'Try copying the notes section by section.',
    },
    patientDeleted: {
      title: 'Patient not found in EHR',
      message:
        'The patient associated with this appointment has been deleted in your EHR. Please ensure the patient exists or select the correct patient in Eluve and try again.',
    },
    appointmentNotFound: {
      title: 'Appointment not found in EHR',
      message:
        'The appointment may have been deleted or rescheduled in your EHR. Please verify the appointment exists and try again.',
    },
    userIdMismatch: {
      title: 'User mismatch detected',
      message:
        'The logged-in user does not match the current EHR user. Re-authenticating automatically.',
    },
    extensionConnectionLost: {
      title: 'Extension connection lost',
      message:
        'The connection to the Eluve extension was lost. Please refresh the page and try again.',
    },
    insufficientStorage: {
      title: 'Recording paused due to low storage',
      message:
        'Your device ran low on storage, so we paused the recording to keep what was captured. Free up space to resume, or finish to generate your note.',
    },
    ehrIntegrationNotAvailable: {
      title: 'EHR Integration Not Available',
      message:
        'This EHR vendor requires browser extension integration which is not available in this context.',
    },
    appointmentAlreadySigned: {
      title: 'Note already signed',
      message:
        'This note is already signed, so the appointment can no longer be recorded or regenerated.',
    },
  },
  sidebar: {
    overview: 'Overview',
    appointments: 'Appointments',
    patients: 'Patients',
    services: 'Services',
    careLibrary: 'Care Library',
    tasks: 'Tasks',
    settings: 'Settings',
    support: 'Support',
  },
  userMenu: {
    impersonatingUser: 'Impersonating User',
    exitImpersonation: 'Exit Impersonation',
    switchOrganization: 'Switch Organization',
    unnamedOrganization: 'Unnamed Organization',
    organizationSettings: 'Organization Settings',
    betaFeatures: 'Beta Features',
    systemStatus: 'System Status',
    contactUs: 'Contact Us',
    logOut: 'Log Out',
    endRecordingToSwitchTitle: 'End recording to switch?',
    endRecordingToSwitchDescription:
      'You have a recording in progress. Switching organizations will end the current recording session.',
    keepRecording: 'Keep recording',
    ending: 'Ending…',
    endAndSwitch: 'End & switch',
  },
  settings: {
    nav: {
      backToApp: 'Back to app',
      account: 'Account',
      preferences: 'Preferences',
      organization: 'Organization',
      userSettings: 'User Settings',
      audioDisplay: 'Audio & Display',
      shortcuts: 'Shortcuts',
      appGrants: 'App Grants',
      note: 'Note',
      tasks: 'Tasks',
      templates: 'Templates',
      chat: 'Chat',
      insights: 'Insights',
      general: 'General',
      members: 'Members',
      sessionTypes: 'Session Types',
      locations: 'Locations',
      services: 'Services',
      providers: 'Providers',
      userGroups: 'User Groups',
      noteRecovery: 'Note Recovery',
      carePlans: 'Care Plans',
    },
    common: {
      save: 'Save',
      add: 'Add',
      delete: 'Delete',
      reset: 'Reset',
      examples: 'EXAMPLES',
      resetToDefaultTitle: 'Reset to Default Settings?',
    },
    user: {
      generalDescription: 'Your basic profile and account information.',
      email: 'Email',
      fullName: 'Full Name',
      specialty: 'Specialty',
      signature: 'Signature',
      providerDetailsTitle: 'Medical Provider Details',
      providerDetailsDescription:
        'Enter your license number, NPI number, and any other relevant details about your medical practice.',
      baaTitle: 'Business Associates Agreement',
      baaSafeguarding:
        'We care about safeguarding your data. Your BAA is part of staying HIPAA compliant.',
      baaRequestPrefix:
        'If your organization requires a separate, signed version of the BAA for record-keeping purposes, you can request one by emailing us at',
      viewBaa: 'View BAA',
      downloadPdf: 'Download PDF',
    },
    audio: {
      microphone: 'Microphone',
      microphoneDescription:
        'Test and pick the audio input used for recordings.',
      notifications: 'Notifications',
      soundEffect: 'Sound Effect',
      soundEffectDescription:
        'Play a chime when starting or stopping a recording.',
      previewSound: 'Preview Sound',
      play: 'Play',
      language: 'Language',
      interfaceLanguageLabel: 'Interface Language',
      interfaceLanguageDescription:
        'Your preferred language for the Eluve interface.',
      interfaceLanguageSearchPlaceholder: 'Search languages...',
      privacyDisplay: 'Privacy & Display',
      privacyMode: 'Privacy Mode',
      privacyModeDescription: 'Hide patient information from your screen.',
      privacyModeToggleHintPrefix: 'Toggle from anywhere with',
      examplePatientName: 'Example patient name:',
      showAppointmentOwners: 'Show Appointment Owners',
      showAppointmentOwnersDescription:
        'Show who owns each appointment in your agenda.',
      shareScreenWithSupport: 'Share screen with support',
      shareScreenDescription:
        'Temporarily monitor interactions for debugging. This will record your interactions on Eluve products for up to {hours} hours and then automatically turn off. These recordings will be saved for up to 30 days in a HIPAA compliant environment and then be automatically deleted.',
      micPreferencesChanged: 'Microphone preferences changed',
      micNotFound: 'Microphone could not be found',
      stopTest: 'Stop',
      test: 'Test',
    },
    note: {
      title: 'Note Preferences',
      defaults: 'Defaults',
      defaultsDescription: 'Common preferences applied to every new note.',
      defaultTemplate: 'Default Template',
      defaultTemplateDescription: 'The note format applied to new sessions.',
      customInstructions: 'Custom instructions',
      customInstructionsDescription:
        'Customize your notes by providing specific guidelines. Try to be as clear and concise as possible for best results.',
      instructionsPlaceholder: 'Type your instructions here',
      saveInstructionsFailed: 'Failed to save instructions',
      exampleRemoveLifestyle: 'Remove parts about lifestyle counseling',
      exampleIncludeQuotes: 'Include patient quotes verbatim',
      examplePlanBullets: 'Keep the plan section bullet-pointed',
      exampleFollowUpTimeline: 'Always end with a follow-up timeline',
    },
    tasks: {
      title: 'Task Preferences',
      customInstructions: 'Task extraction instructions',
      customInstructionsDescription:
        'Describe which follow-up tasks matter for your workflow, what to include, skip, or emphasize. Applied every time tasks are extracted from your sessions.',
      instructionsPlaceholder: 'Type your instructions here',
      saveInstructionsFailed: 'Failed to save instructions',
    },
    appGrants: {
      authorizedAgents: 'Authorized agents',
      authorizedAgentsDescription:
        'Agents you have authorized to act on your behalf. Revoking forces the agent to ask for consent again before it can run.',
    },
    organization: {
      layers: 'Layers',
      layersDescription: 'Organize Eluve to best fit your practice.',
    },
    chat: {
      title: 'Chat Preferences',
      resetSuccess: 'Settings reset to defaults',
      resetFailed: 'Failed to reset settings',
      outputPreferences: 'Output Preferences',
      outputPreferencesSavedOne:
        '{count} saved preference for response format and detail level.',
      outputPreferencesSavedMany:
        '{count} saved preferences for response format and detail level.',
      outputPreferencesConfigure:
        'Configure response format, detail level, and custom instructions.',
      customInstructions: 'Custom Instructions',
      customInstructionsDescription:
        'Extra guidance every chat response should follow.',
      conversationStarters: 'Conversation Starters',
      conversationStartersDescription:
        'Prompts suggested when starting a new chat.',
      knowledgeBase: 'Knowledge Base',
      knowledgeBaseDescription:
        'Select which knowledge sources to include in chat context.',
      resetToDefault: 'Reset to Default',
      resetDialogDescription:
        'This will delete all your custom chat settings including custom instructions, conversation starters, and uploaded files. The chat will revert to default settings. This action cannot be undone.',
      customInstructionsPageTitle: 'Chat Custom Instructions',
      saveInstructionsFailed: 'Failed to save custom instructions',
      exampleRemoveParts: 'Remove parts about...',
      exampleIncludeDiscussion: 'Include discussion about...',
      exampleMoreConcise: 'Make notes more concise',
      exampleMoreDetailsPlan: 'Add more details in the plan section',
      outputPreferencesPageDescription:
        'Configure response format, detail level, and custom instructions. Link them to conversation starters.',
      preferenceCountOne: '{count} preference',
      preferenceCountMany: '{count} preferences',
      editPreferenceLabel: 'Edit {label}',
      deletePreferenceLabel: 'Delete {label}',
      noOutputPreferences: 'No output preferences configured',
      newOutputPreference: 'New Output Preference',
      editOutputPreference: 'Edit Output Preference',
      deletePreferenceTitle: 'Delete Preference?',
      deletePreferenceDescription:
        'This will delete this output preference. Any conversation starters linked to it will be unlinked. This action cannot be undone.',
      savePreferenceFailed: 'Failed to save preference',
      deletePreferenceFailed: 'Failed to delete preference',
      updateInstructionsFailed: 'Failed to update custom instructions',
      updateContextSourcesFailed: 'Failed to update context sources',
      updateStartersFailed: 'Failed to update conversation starters',
      updateFilesFailed: 'Failed to update files',
    },
    insights: {
      title: 'Insights Preferences',
      savePreferences: 'Save Preferences',
      savePreferencesFailed: 'Failed to save preferences',
      preferencesSaved: 'Preferences saved',
      previewAgainst: 'Preview against',
      samplePatient: 'Sample patient (Edward Hyde)',
      generatePreview: 'Generate Preview',
      generatingPreview: 'Generating preview…',
      previewOutOfDate:
        'Preview is out of date — click Generate to apply your changes.',
      noRegenNeeded: 'No upcoming appointments needed regeneration',
      regeneratingOne: 'Regenerating insights for {count} appointment',
      regeneratingMany: 'Regenerating insights for {count} appointments',
      regenerateFailed: 'Failed to regenerate insights',
      resetFailed: 'Failed to reset preferences',
      unsavedChangesTitle: 'Unsaved Changes',
      unsavedChangesDescription:
        'You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.',
      stay: 'Stay',
      leave: 'Leave',
      regenerateDialogTitle: 'Regenerate PVI?',
      regenerateDialogDescriptionOne:
        'Your changes will apply to new appointments starting tomorrow. Do you want to regenerate AI Insights for your {count} upcoming appointment?',
      regenerateDialogDescriptionMany:
        'Your changes will apply to new appointments starting tomorrow. Do you want to regenerate AI Insights for your {count} upcoming appointments?',
      notNow: 'Not now',
      regenerateNow: 'Regenerate now',
      resetDialogDescription:
        'This will delete your custom insights preferences and revert to default settings. This action cannot be undone.',
      previewInvalidResponse:
        'Preview returned an invalid response. Please try again.',
      previewGenerationFailed: 'Preview generation failed. Please try again.',
      previewTimedOut: 'Preview generation timed out. Please try again.',
      previewStartFailed:
        'Unable to start preview generation. Please try again.',
    },
  },
  appointmentPage: {
    gotIt: 'Got it',
    save: 'Save',
    retry: 'Retry',
    tryAgain: 'Try Again',
    areYouSure: 'Are you sure?',
    noLongerEditable: 'You will no longer be able to edit this note in Eluve.',
    signNote: 'Sign Note',
    previewPdf: 'Preview PDF',
    pdfPrepareFailed: 'Failed to prepare PDF',
    experiments: 'Experiments',
    canceledInEhr: 'This appointment was canceled in your EHR.',
    summaryReadyTitle: 'Summary is ready!',
    summaryReadyDescription: 'Your summary is ready to be reviewed.',
    summariesReadyTitle: 'Summaries are ready!',
    summariesReadyDescription: 'Your summaries are ready to be reviewed.',
    appointmentsHeading: 'Appointments',
    appointmentsLoadFailed: 'Failed to load appointment data.',
    saveBlockFailed: 'Failed to save block',
    improveYourNotes: 'Improve your notes',
    saveSummaryFailed: 'Failed to save summary',
    disclaimer: 'Disclaimer',
    details: {
      title: 'Details',
      appointmentHeading: 'Appointment',
      date: 'Date',
      time: 'Time',
      location: 'Location',
      type: 'Type',
      owner: 'Owner',
      artifacts: 'Artifacts',
      updateFailed: 'Unable to update appointment details',
      noLocationSpecified: 'No location specified',
      noDateSpecified: 'No date specified',
      startTime: 'Start Time',
      end: 'End',
      noType: 'No Type',
    },
    header: {
      selectPatientFirst: 'Please select a patient first!',
      appointmentDetailsAria: 'Appointment details',
    },
    emailSuperbill: {
      emailSubject: 'Your Superbill for {date} - {tenantName}',
      emailBodyWithDoctor:
        'Hello{name}, attached is your Superbill for your visit with Dr. {doctorName} on {date}.',
      emailBody:
        'Hello{name}, attached is your Superbill for your visit on {date}.',
      updateEmailFailed: "The patient's email could not be updated.",
      sendFailed: 'The email could not be sent. Please try again later!',
      sentTitle: 'Email Sent',
      sentDescription: 'Your email has been successfully sent out.',
      emailPdfTooltip: 'Email PDF',
      title: 'Email Patient',
      description: 'Please review the information before sending',
      emailAddress: 'Email Address',
      updatePatientEmailLabel:
        "Also update the patient's email on their account",
      subjectLabel: 'Subject',
      subjectPlaceholder: 'Your superbill',
      messageLabel: 'Message',
      messagePlaceholder: 'Description',
      sendEmail: 'Send Email',
    },
    syncToEhr: {
      noteSignedSuccessfully: 'Note Signed Successfully',
      noteDraftedSuccessfully: 'Note Drafted Successfully',
      noteSynced: 'Your note has been synced.',
      signFailed: 'Failed to sign note.',
      syncedToEhr: 'Your note has been successfully synced to {ehrName}.',
      syncedButSignFailed:
        'Your note has been successfully synced to {ehrName}, but we experienced an error signing the note on Eluve.',
      syncNotAvailable: 'Sync to EHR is not available',
      syncToEhrButton: 'Sync to EHR',
      signExternalChart: 'Sign external chart',
      exportedTo: 'The contents of this note will be exported to {ehrName}',
      asA: 'as a',
      signedNote: 'signed note',
      draftNote: 'draft',
      forPatient: 'for patient',
      confirmSyncAndSign: 'Yes, Sync and Sign Note',
      confirmSyncAsDraft: 'Yes, Sync Note as Draft',
      syncingTo: 'Syncing to {ehrName}',
      syncingDescription:
        'We are in the process of syncing to {ehrName}. This should only take a few moments.',
      addChartUrlTitle: 'Add Chart URL',
      chartUrlPlaceholder: 'Chart URL',
      addChartUrlAndSign: 'Add Chart URL and Sign',
      manuallyAddChartUrl: 'Manually Add Chart URL',
    },
    signNoteOnEluve: {
      noteSigned: 'Your note has been signed.',
      notOwner: 'Only the appointment owner may sign this note.',
      chartNotModified:
        "The chart hasn't been modified. Please fill out the chart before signing the note.",
      selectLocation: 'Select a location before signing the note.',
      selectPatient: 'Select a patient before signing the note.',
      confirmSignNote: 'Yes, Sign Note',
    },
    signedSummary: {
      cancelAmendmentFailed: 'Failed to cancel amendment',
      submitAmendmentFailed: 'Failed to submit amendment',
      discardAmendment: 'Discard Amendment',
      amendChart: 'Amend Chart',
    },
    audioLost: {
      title: 'This appointment may be missing audio',
      lastAudioReceived:
        "The last audio we received was {ago} — it looks like the device went to sleep or the browser was suspended after that. Any speech during that time was not captured and can't be recovered.",
      noAudioSinceStart:
        "We started recording {ago} but haven't received any audio since — this usually happens when the device goes to sleep or the browser is suspended. If you were speaking, that audio was not captured and can't be recovered.",
      avoidNextTime: 'To avoid this next time:',
      onComputerLabel: 'On a computer:',
      onComputerBody:
        "keep this browser tab open and in the foreground while recording — don't switch to another tab or let the computer sleep.",
      onPhoneLabel: 'On a phone or tablet:',
      onPhoneBody:
        "keep the browser open and turn off auto-lock / screen lock so the screen doesn't turn off while recording.",
      moreThanADayAgo: 'more than a day ago',
      aboutSecondsAgoOne: 'about 1 second ago',
      aboutSecondsAgo: 'about {count} seconds ago',
      aboutMinutesAgoOne: 'about 1 minute ago',
      aboutMinutesAgo: 'about {count} minutes ago',
      aboutHoursAgoOne: 'about 1 hour ago',
      aboutHoursAgo: 'about {count} hours ago',
      agoAtTime: '{ago} (at {time})',
    },
    presence: {
      title: 'This Session Is Longer Than Usual',
      beforeCountdown:
        'If you would like to continue recording, please click the button below. Otherwise, in',
      afterCountdown:
        'the session will be automatically paused. You can still resume it later if needed.',
    },
    micFailed: {
      title: 'Your Microphone Got Disconnected',
      description:
        "Recording has been paused. Before resuming ensure the microphone selected below is the one you'd like to use.",
      stayPaused: 'Stay Paused',
    },
    recordingFailed: {
      title: 'Session is no longer in progress.',
      description: 'Refresh the page to resume the session.',
      refreshPage: 'Refresh Page',
    },
    storageFailure: {
      quotaTitle: 'Your browser storage is full',
      quotaDescription:
        'Recording was paused, but the audio captured so far is saved. Free up disk space or refresh the page to continue. You can also generate a note now.',
      unhealthyTitle: 'Your browser storage is unavailable',
      unhealthyDescription:
        'Recording was paused, but the audio captured so far is saved. Refresh the page to continue, or try another browser if this keeps happening. You can also generate a note now.',
      continue: 'Continue',
      refreshPage: 'Refresh Page',
      resumeBlockedTooltip:
        'Browser storage is full or unavailable. Refresh the page to resume.',
    },
    newAppointment: {
      sessionSettings: 'Session Settings',
      noteSettings: 'Note Settings',
      viewMoreOptions: 'View More Options',
      viewLessOptions: 'View Less Options',
      playDisclaimer: 'Play disclaimer before recording',
      startSession: 'Start Session',
      selectMicrophoneTooltip: 'Select a microphone to start the session.',
      ownerOnlyStartTooltip:
        'Only the appointment owner can start the session.',
      micAccessTooltip:
        'Could not access the microphone. Please check your settings',
      noAudioTooltip: 'No audio detected. Check your microphone and volume.',
      alreadyInSessionTooltip: 'An appointment is already in session.',
      sessionStartingTooltip: 'Session is starting, please wait...',
      manualChartingStartFailed: 'Failed to start session for manual charting.',
      duplicatePreviousChart: 'Duplicate Previous Chart',
      selectChart: 'Select Chart',
      duplicateChartTitle: 'Duplicate Chart',
      duplicateChartDescription: 'Select the chart you want to duplicate',
      transcribeTab: 'Transcribe',
      manualChartingTab: 'Manual Charting',
    },
    chiefComplaint: {
      updateFailed: 'The appointment details could not be updated.',
      updateContext: 'Update Context',
      addContext: 'Add Context',
      title: 'Chief Complaint & Reason For Visit',
      chiefComplaintLabel: 'Chief Complaint',
      chiefComplaintPlaceholder: 'Enter chief complaint',
      reasonForVisitLabel: 'Reason For Visit',
      reasonForVisitPlaceholder: 'Enter reason for visit',
    },
    summaryStates: {
      pendingDataDescription: "We'll generate your note shortly.",
      takingLongerDescription:
        "We're still working on your notes. You can leave this page and start a new session! We'll let you know when your note is ready.",
      browserOnDevice: '{browser} on {device}',
      uploadNeededPrefixOne: 'Your recording still needs to be uploaded from',
      uploadNeededPrefixMany: 'Your recordings still need to be uploaded from',
      uploadNeededSuffixOne:
        '. To generate your note, keep Eluve open in that browser until it completes.',
      uploadNeededSuffixMany:
        '. To generate your note, keep Eluve open in those browsers until they complete.',
    },
    summaryFailed: {
      resumeRecording: 'Resume recording',
    },
    previsitInsights: {
      title: 'Pre-Visit Insights',
      tooltip: 'AI-generated summary of the patient’s prior visit notes.',
      autoGenerateNotice:
        'Pre-Visit Insights generate automatically about {minutes} minutes before the visit.',
      generateNow: 'Generate now',
      selectPatient: 'Select a patient to generate pre-visit insights.',
    },
    documents: {
      deleted: 'Document deleted',
      deleteFailed: 'Failed to delete document',
      renameFailed: 'Failed to rename document',
      newDocument: 'New document',
      generated: 'Document generated',
      unknownError: 'An unknown error occurred.',
      selectPatientFirst: 'Select a patient first to create documents',
    },
    carePlan: {
      preparing: 'Preparing your care plan…',
      willBeGenerated:
        'The care plan will be generated from the applied findings.',
    },
    filesDrawer: {
      trigger: 'Files',
      title: 'Appointment Files',
      filesWillBeRemoved:
        'Files will be removed from your device once the appointment is complete',
      noLocalFiles: 'There are no local files on this device.',
      download: 'Download',
    },
    additionalNotes: {
      updateFailed: 'Failed to update additional notes',
    },
    summaryComparison: {
      loading: 'Loading Summary Comparison...',
      returnToSession: 'Return to your session',
      defaultTemplateTooltip:
        'This is set as your default template. You can change it in your settings.',
      select: 'Select',
      generating: 'Generating...',
      generateSummaries: 'Generate Summaries',
      inactiveTemplate: 'Inactive Template',
      useThisSummary: 'Use this summary',
      alreadySigned: 'This note is already signed',
      alreadyCurrent: 'This note is already set as current summary',
    },
    rerunTranscription: {
      rerun: 'Rerun transcription on audio file',
      inProgress: 'Currently transcribing audio file',
    },
    manualCharting: {
      automateNotesTitle: 'Want to automate your notes instead?',
      automateNotesDescription:
        'You can start recording but it will overwrite your existing manual notes.',
      startRecording: 'Start Recording',
    },
    controls: {
      resumeSession: 'Resume Session',
      generateNote: 'Generate Note',
      pause: 'Pause',
      resume: 'Resume',
      processing: 'Processing',
      anotherAppointmentInSession: 'Another appointment in session',
    },
    clientRecap: {
      emptySummary: 'Client recap summary is empty',
      emailSubject: "Follow up from today's session ({date})",
      copiedToClipboard:
        'Email body copied to clipboard — paste it into your email',
      sendMailTooltip: 'Send mail to patient',
    },
    sampleTranscript: {
      addFailed: 'Sample transcript could not be added.',
      prompt:
        "Can't record an appointment right now? Try a sample transcript to see how Eluve works!",
      tryButton: 'Try a transcript',
    },
    superbill: {
      title: 'Superbill',
      viewPdf: 'View PDF',
    },
    uploadAudio: {
      unsupportedFormat: 'Unsupported file format',
      fileSystemUnavailable: 'File system not available',
      uploadFailed: 'Failed to upload file',
      uploadFile: 'Upload File',
      uploadingRecording: 'Uploading Recording',
    },
    versionHistory: {
      title: 'Version History',
      startedSession: 'started the session',
      endedSession: 'ended the session',
      editedChart: 'edited the chart',
      signedChart: 'signed the chart',
      amended: 'amended',
      signedWithAmendments: 'signed with amendments',
      lastEditedChart: 'last edited the chart',
      generatedSuperbill: 'generated the superbill',
      updatedSuperbill: 'updated the superbill',
    },
    lastSaved: {
      lastAmendedBy: 'Last amended by {name} at {time}',
      lastSignedBy: 'Last signed by {name} at {time}',
      lastSavedAt: 'Last saved at {time}',
      saving: 'Saving...',
    },
    overwriteSummary: {
      title: 'Overwrite existing note?',
      description:
        'If you generate a new note your existing note will be overwritten. Is that ok?',
      no: 'No',
      confirm: 'Yes, Proceed to New Note',
    },
    insights: {
      reasonForVisitTab: 'Reason for Visit',
      considerationsTab: 'Considerations',
      generateFailed:
        'We could not generate your insights. Please try again later!',
      generateOutputFailed: 'Error generating output. Please try again later.',
      eluveInsights: 'Eluve Insights',
      tagline:
        'Improve diagnostic accuracy and patient outcomes with AI-powered insights.',
      run: 'Run Insights',
      rerun: 'Re-run Insights',
      expand: 'Expand to full screen',
      collapse: 'Exit full screen',
      noInsightsTitle: 'No insights yet',
      noInsightsDescription:
        "We couldn't generate any insights based on the information currently available. Try again once the conversation is longer.",
    },
    redFlags: {
      wasHelpful: 'Was this insight helpful?',
      noneTitle: 'No red flags identified',
      noneDescription:
        'Review the Differential Diagnosis tab to see how the key indicators were assessed based on the conversation and evidence.',
      viewDdx: 'View DDx',
    },
    waveform: {
      muted: 'Muted',
    },
  },
  home: {
    toasts: {
      failedToRetrieveAppointments: 'Failed to retrieve appointments',
    },
    calendarSidebar: {
      title: 'Calendar',
    },
    snippets: {
      title: 'Snippets',
      descriptionPrefix:
        'Create reusable text to streamline your documentation. To insert a snippet, type',
      descriptionSuffix: 'in the editor and select your shortcut.',
      countLabelOne: '{count} snippet',
      countLabelMany: '{count} snippets',
      actionsAriaLabel: 'Snippet actions',
      export: 'Export',
      import: 'Import',
      createNew: 'Create New',
      loading: 'Loading ...',
      noSnippets: 'No snippets configured',
      editSnippet: 'Edit {title}',
      deleteSnippet: 'Delete {title}',
      importSuccess: 'Imported {count} snippets successfully',
      importParseError: 'Error parsing snippets file',
    },
    transcriptionConfidence: {
      tooFarTitle: 'Are you too far from your device?',
      tooFarDescription:
        'Try moving your device closer so that we can properly pick up the audio.',
    },
    ehrImport: {
      noEhrAssociationsTitle: 'No EHR associations',
      noEhrAssociationsDescription:
        'Locations are not associated with an external EHR. Please ask your admin to associate a location with an EHR.',
      successTitle: 'Success!',
      importedFrom: 'Imported patients and appointments from:',
      failedImportingFrom: 'Failed importing from:',
      failedTitle: 'Failed to import data',
      failedImportingPatientsFrom:
        'Failed importing patients and appointments from:',
      refreshAndTryAgain: 'Refresh the page and try again.',
    },
    ehrOnboarding: {
      allSetTitle: "You're all set!",
      allSetDescription:
        'Your EHR integration is all set up! You can now see your upcoming appointments on the homepage.',
      done: 'Done',
      importingTitle: 'Importing appointments',
      importingDescription:
        'It takes a few seconds for the first import, but afterwards we will automatically sync your EHR.',
      importing: 'Importing...',
      importTitle: 'Import appointments',
      importDescription:
        'Import EHR data so that Eluve can work seamlessly with your workflow',
      startImport: 'Start Import',
      dataSafety: 'Learn how Eluve keeps your data safe',
      extensionInstalledTitle: 'Extension installed!',
      connectEhr: 'Connect your EHR account with Eluve',
      selectLocationPlaceholder: 'Select your {tenantName} location',
      noLocationsFound: 'No locations found',
      contactAdminForLocation:
        'Please contact organization admin to setup a location',
      next: 'Next',
    },
    emptyState: {
      noUpcomingAppointments: 'No upcoming appointments',
      finishSetupDescription:
        'Finish setting up your EHR integration to sync your appointments with Eluve.',
      importAppointments: 'Import Appointments',
      newSession: 'New Session',
      noUpcomingNext7Days:
        'You don’t have any upcoming appointments for the next 7 days. If you recently added a new appointment on your EHR that is not showing up you can try refreshing.',
      refresh: 'Refresh',
      troubleshoot: 'Troubleshoot',
      downloadExtensionDescription:
        "You don't have any appointments. Download the Eluve extension to get seamless integration.",
      installExtension: 'Install Extension',
      startRecordingSession: 'Start a recording session',
    },
    ehrImporter: {
      installExtensionTitle: 'Install the extension',
      installExtensionDescription:
        'Get the chrome extension to easily sync your note back to your EHR.',
      ehrIntegration: 'EHR Integration',
      almostThere: "You're almost there! Finish setting up your integration!",
      accountsConnected: '{count} accounts connected',
      problemsSyncing: 'Problems syncing with your EHR',
      completeOnboarding: 'Complete onboarding',
      importPatientsAppointments: 'Import patients and appointments',
    },
    agenda: {
      patientNotSpecified: 'Patient Not Specified',
    },
    loader: {
      loadingAppointments: 'Loading Appointments ...',
    },
    tenantsList: {
      pendingApproval: 'Pending Approval',
      organization: 'Organization',
      role: 'Role',
      memberSince: 'Member Since',
      createdAt: 'Created at',
    },
  },
  tenantAdmin: {
    common: {
      save: 'Save',
      saveChanges: 'Save Changes',
      create: 'Create',
      add: 'Add',
      users: 'Users',
      areYouSure: 'Are you sure?',
      thereWasAnError: 'There was an error',
      invalidFileExtension: 'Invalid file extension.',
      actionsColumn: 'Actions',
      detailsColumn: 'Details',
      nameColumn: 'Name',
    },
    location: {
      editName: 'Edit location name',
      detailsUpdated: 'Location details updated',
      detailsUpdateFailed: 'Failed to update location details',
      logoUpdated: 'Logo updated',
      logoUpdateFailed: 'Failed to update logo',
      logoUploadFailed: 'Failed to upload logo',
      displayNameUpdated: 'Display name updated',
      displayNameUpdateFailed: 'Failed to update display name',
      changeLogoTooltip: 'Click to change logo',
      uploadLogoTooltip: 'Click to upload logo',
      logoAlt: '{name} logo',
      uploadLogo: 'Upload logo',
      removeLogo: 'Remove logo',
      autoCreatedBadge: 'Auto-created',
      renamedBadge: 'renamed',
      detailsTitle: 'Location Details',
      hideAdvancedSettings: 'Hide Advanced Settings',
      showAdvancedSettings: 'Show Advanced Settings',
      externalEhrLabel: 'External EHR',
      selectEhr: 'Select EHR',
      removeEhrTooltip: 'Remove EHR from location',
      or: 'OR',
      createNewEhr: 'Create New EHR',
      externalIdLabel: 'External ID',
      addressLabel: 'Address',
      phoneNumberLabel: 'Phone Number',
      inheritedDefaultsTitle: 'Inherited defaults',
      inheritedDefaultsDescription:
        "Applied to this location and inherited by descendant locations that don't set their own.",
      businessNameLabel: 'Business Name',
      posCodeLabel: 'Place of Service (POS) Code',
      taxIdLabel: 'Tax ID',
      replyToEmailLabel: 'Reply-to Email',
      addUser: 'Add User',
      billingCodesTitle: 'Billing Codes',
      usersStatDescription: 'With access to this location',
      appointmentsStatTitle: 'Appointments',
      appointmentsStatDescription: 'Recorded at this location',
      sourceOrgIdLabel: 'Source Organization ID',
      sourceOrgIdHint: 'Match & dedupe key — read-only',
      sourceNameLabel: 'Source name (OrganizationName)',
      tabDetails: 'Details',
      tabUserPermissions: 'User permissions',
      tabPeople: 'People',
      tabServices: 'Services',
      tabBillingCodes: 'Billing codes',
      schedulingTitle: 'Scheduling',
      schedulingTimezoneLabel: 'Scheduling timezone',
      openingHoursLabel: 'Opening hours',
      schedulingUnavailable: 'FHIR session unavailable.',
      schedulingLoading: 'Loading…',
      schedulingNotMirrored: "This location isn't mirrored for scheduling yet.",
      schedulingNoTimezone:
        "Without a timezone this clinic can't be booked — providers' hours have no grid to land on.",
      practiceRolesTitle: 'Practice roles at this location',
      practiceRolesDescription:
        "Front desk books appointments; clinicians also own clinical documentation. Default follows the member's org-wide role.",
      practiceRolesLoadFailed:
        "Couldn't load practice roles — editing is disabled.",
      practiceRoleDefault: 'Default',
      practiceRoleUnavailable: 'Unavailable',
      practiceRoleClinician: 'Clinician',
      practiceRoleFrontDesk: 'Front desk',
      practiceRoleSet: 'Role set to {role}.',
      practiceRoleCleared: 'Role cleared.',
      practiceRoleAccessSyncFailed:
        'Their access permissions did not update — use Re-sync policies.',
      practiceRoleSetFailed: "Couldn't set role: {message}",
      practiceRoleUnknownError: 'unknown error',
      practiceRoleApiUnreachable: 'Failed to reach API',
    },
    locations: {
      title: 'Locations',
      description:
        'Locations synced from your EMR appear below — rename any location, or switch to the Tree view to manage the hierarchy.',
      hubDescription:
        'Your practice, in one place — pick a location to manage its details, people, and scheduling.',
      hubEmpty: 'No locations yet.',
      addLocation: 'Add location',
      addLocationParentLabel: 'Parent',
      addLocationParentPlaceholder: 'Select parent',
      addLocationParentSearch: 'Search locations',
      addLocationNameLabel: 'Name',
      addLocationCancel: 'Cancel',
      addLocationSubmit: 'Add',
      locationCreated: "Location '{name}' created",
      locationCreateFailed: "Failed to create location '{name}'",
      rootFallback: 'Root',
      empty: 'No locations yet — switch to the Tree view to add one.',
      locationColumn: 'Location',
      sourceOrgIdColumn: 'Source Org ID',
      apptsColumn: 'Appts',
      openLocationAria: 'Open {name}',
      loadFailed: 'Failed to load locations',
      tableView: 'Table',
      treeView: 'Tree',
    },
    newEhr: {
      title: 'Create new EHR',
      vendorLabel: 'Vendor',
      selectVendor: 'Select Vendor',
      domainLabel: 'Domain',
      domainPlaceholder: 'Enter Domain',
      secretConfiguration: 'Secret Configuration',
      plaintextConfiguration: 'Plaintext Configuration',
      enterField: 'Enter {field}',
      addNewEhr: 'Add new EHR',
    },
    users: {
      inviteUser: 'Invite User',
      inviteSendFailed: 'Invitation could not be sent.',
      inviteNewUserTitle: 'Invite New User',
      userRoleLabel: 'User Role',
      roleAdmin: 'Admin',
      roleMember: 'Member',
      roleDescription:
        'Admins can view all patients and appointments, as well as manage users and settings for the organization. Note: you can change the role of a user later.',
      emailLabel: 'Email Address',
      emailPlaceholder: 'john.doe@example.com',
      firstNameLabel: 'First Name',
      firstNamePlaceholder: 'John',
      lastNameLabel: 'Last Name',
      lastNamePlaceholder: 'Doe',
      locationLabel: 'Location',
      selectLocation: 'Select a location',
      sendInvitation: 'Send Invitation',
      inviteResendFailed: 'Invite email could not be resent.',
      inviteRevokeFailed: 'Invite could not be revoked.',
      resendEmail: 'Resend email',
      revokeInvite: 'Revoke invite',
      resendConfirmation:
        'This will send another invitation email to "{email}".',
      revokeConfirmation:
        'This will block the user ({email}) from accepting the invite.',
      resendEmailAction: 'Resend Email',
      revokeInviteAction: 'Revoke Invite',
      excludeFailed: 'User could not be excluded from usage reports.',
      includeFailed: 'User could not be included in usage reports.',
      emailDescription: 'Email: {email}',
      removeFailed: 'User could not be removed from the organization.',
      roleChangeFailed: "User's role could not be changed.",
      changeToAdmin: 'Change to admin',
      changeToMember: 'Change to member',
      includeInUsageReports: 'Include in usage reports',
      excludeFromUsageReports: 'Exclude from usage reports',
      grantReportingAccess: 'Grant reporting access',
      revokeReportingAccess: 'Revoke reporting access',
      reportingAccessFailed: "User's reporting access could not be changed.",
      reportingAccessColumn: 'Reporting access',
      reportingAccessGranted: 'Granted',
      reportingAccessNotGranted: 'Not granted',
      reportingAccessAdminImplicit: 'Granted (admin)',
      removeUser: 'Remove user',
      removeConfirmation:
        'This will remove the user ({email}) from your organization.',
      roleChangeConfirmation:
        'This will change the role of the user ({email}) to "{role}".',
      excludeConfirmation:
        "This user will be hidden from this list and won't count toward usage reports.",
      removeUserAction: 'Remove User',
      changeRoleAction: 'Change Role',
      excludeUserAction: 'Exclude User',
      fullNameColumn: 'Full Name',
      emailColumn: 'Email',
      roleColumn: 'Role',
      inviteStatusColumn: 'Invite Status',
      statusColumn: 'Status',
      lastSeenColumn: 'Last Seen',
      joinInviteDateColumn: 'Join/Invite Date',
      invitePendingTag: 'Invite Pending',
      inviteRevokedTag: 'Invite Revoked',
      inviteExpiredTag: 'Invite Expired',
      showExcludedUsers: 'Show excluded users',
      excludedUsersTooltip: 'Excluded users do not count toward usage reports',
      searchPlaceholder: 'Search users by name or email...',
      membersTitle: 'Members',
    },
    userGroups: {
      title: 'User Groups',
      createFailed: 'Failed to create user group',
      createUserGroup: 'Create User Group',
      newUserGroupTitle: 'New User Group',
      groupNamePlaceholder: 'Enter group name',
      removeUserFailed: 'We could not remove the user from the group.',
      removeUserTooltip: 'Remove user from this group',
      addUserFailed: 'We could not add the user to the group.',
      selectUserToAdd: 'Select a user to add',
      searchUsers: 'Search users',
      noUsersToAdd: 'No users available to add.',
      noUsersInGroup: 'No users in this group yet.',
    },
    codes: {
      medicalCodeConfigurationsTitle: 'Medical Code Configurations',
      bulkActions: 'Bulk Actions',
      addIndividualCodes: 'Add Individual Codes',
      medicalCodes: 'Medical Codes',
      codeTypeColumn: 'Code Type',
      codeColumn: 'Code',
      labelColumn: 'Label',
      priceColumn: 'Price',
      removeConfigFailed: 'Failed to remove user group config',
      removeCodeTooltip: 'Remove code from this group',
      configCreated: 'User group config created successfully',
      configCreateFailed: 'Failed to create user group config',
      newConfigTitle: 'New Billing Code Configuration',
      customLabel: 'Custom Label',
      customLabelDescription: 'If not provided, the existing label is used.',
      priceRequiredLabel: 'Price (Required)',
      modifiersLabel: 'Modifiers',
      modifiersDescription: 'A comma separated list of modifiers.',
      downloadingTitle: 'Downloading Medical Codes',
      preparingDownload: 'Preparing your download...',
      downloadComplete: 'Download complete!',
      downloadFailed: 'Failed to download medical codes. Please try again.',
      downloadAllCodes: 'Download All Available Codes',
      downloadEnabledCodes: 'Download Enabled Codes',
      uploadTitle: 'Upload Medical Codes',
      uploadInstructions:
        'Please select a CSV file with medical codes to upload. The file should include the following columns:',
      uploadColumnCode: 'The medical code (required)',
      uploadColumnType: 'Code type, should be one of: {types} (required)',
      uploadColumnDescription: 'Description for the code (optional)',
      uploadColumnPrice:
        "Price per unit in dollars e.g. '60' or '120.50' (optional)",
      uploadColumnModifiers: 'Comma-separated list of modifiers (optional)',
      uploadWarning:
        'Warning: Uploading a new file will replace ALL existing code configurations for this group.',
      selectFile: 'Select File',
      uploadFileLimits: 'Maximum file size: 5MB, CSV format only',
      uploadingFile: 'Uploading your file...',
      processingCodes: 'Processing medical codes, this may take a moment...',
      uploadComplete: 'Upload complete!',
      successfullyProcessed: 'Successfully processed:',
      codesCount: '{count} codes',
      skipped: 'Skipped:',
      errors: 'Errors:',
      moreErrors: '...and {count} more errors',
      uploadFailed: 'Failed to upload medical codes',
      uploadCodes: 'Upload Codes',
    },
    noteRecovery: {
      title: 'Note Recovery',
      description:
        'Regenerate clinical notes for appointments where the note failed, was never generated, or is stuck. Only appointments from the last 7 days are shown — notes older than that can’t be regenerated here. Appointments with a signed note are excluded; turn on “Show appointments with notes” to also include ones that already have an unsigned draft.',
      regenerateNote: 'Regenerate note',
      patientColumn: 'Patient',
      startDateColumn: 'Start Date',
      userColumn: 'User',
      statusColumn: 'Status',
      filterByUser: 'Filter by user',
      filterByStatus: 'Filter by status',
      allUsers: 'All users',
      allStatuses: 'All statuses',
      showWithNotes: 'Show appointments with notes',
      regenerationStarted: 'Note regeneration started',
      notPermitted: 'You are not permitted to regenerate notes for this tenant',
      alreadySigned: 'This appointment already has a signed note',
      regenerateFailed: 'Failed to regenerate note',
      regenerateDescriptionPrefix: 'Regenerate the clinical note for',
      regenerateDescriptionSuffix: 'using the selected template bundle.',
      templateBundle: 'Template bundle',
      selectTemplateBundle: 'Select a template bundle',
      untitledBundle: 'Untitled bundle',
      noBundles: 'No enabled template bundles are available for this tenant.',
      activeWarningPrefix:
        'This appointment is still active. Generating a note will',
      activeWarningBold: 'complete the appointment',
      activeWarningSuffix: 'and end the session.',
      activeAcknowledge:
        'I understand this will complete the active appointment.',
    },
    sessionTypes: {
      title: 'Session Types',
      updated: 'Updated session type',
      updateFailed: 'Failed to update session type',
      editTooltip: 'Edit this Session Type',
      updateTitle: 'Update Session Type',
      isInitialVisitTypeLabel: 'Is Initial Visit Type',
      contextColumn: 'Context',
      contextPlaceholder: 'Description',
      isInitialVisitColumn: 'Is Initial Visit',
    },
    general: {
      title: 'General',
      logoLabel: 'Logo',
      organizationNameLabel: 'Organization Name',
      interfaceLanguageLabel: 'Interface Language',
      interfaceLanguageDescription:
        "The default UI language for this organization's users. Those who have explicitly chosen their own language are not affected.",
      interfaceLanguageUpdateFailed: 'Failed to update interface language',
      changeInterfaceLanguageTitle: 'Change Interface Language',
      changeInterfaceLanguagePrefix: 'This will change the UI for',
      someUsers: 'some users',
      userCountOne: '{count} user',
      userCountMany: '{count} users',
      changeInterfaceLanguageSuffix:
        'in this organization, since users who have explicitly chosen a language are not affected.',
      editInterfaceLanguage: 'Edit interface language',
      logoUpdateFailed: 'Failed to update organization logo',
      logoDeleteFailed: 'Failed to delete organization logo.',
      organizationLogoAlt: 'Organization logo',
      nameUpdateFailed: 'Failed to update organization name',
      editNameTitle: 'Edit Organization Name',
      namePlaceholder: 'Enter organization name',
      nameInputAria: 'Organization name',
      editName: 'Edit organization name',
    },
  },
  patients: {
    common: {
      loading: 'Loading…',
      checkingAccess: 'Checking access…',
      noFhirRecord: 'No FHIR record is available for this patient.',
      unknownError: 'Unknown error',
      saving: 'Saving…',
      saveChanges: 'Save changes',
      keepEditing: 'Keep editing',
    },
    list: {
      title: 'Patients',
      loadFailed: 'Failed to load patient data.',
      searchPlaceholder: 'Search...',
      details: 'Details',
      firstName: 'First Name',
      lastName: 'Last Name',
      lastAppointment: 'Last Appointment',
      dateOfBirth: 'Date of Birth',
      importedFrom: 'Imported From',
      appointmentsCount: '# Appointments',
      copyExternalId: 'Copy Patient External ID',
      includeInactive: 'Include inactive/deceased',
    },
    tabs: {
      overview: 'Overview',
      appointments: 'Appointments',
      documents: 'Documents',
      importedNotes: 'Imported Notes',
      artifacts: 'Artifacts',
      carePlans: 'Care Plans',
      medicalProfile: 'Medical Profile',
      careTeam: 'Care Team',
      observations: 'Observations',
      patientUploads: 'Patient Uploads',
    },
    detailsPage: {
      copyExternalId: 'Copy External ID',
      viewOnVendor: 'View on {vendor}',
      dateOfBirth: 'Date of Birth:',
      age: 'Age:',
      yearsOld: '{age} years old',
      error: 'Error',
    },
    importedNotes: {
      viewPdf: 'View PDF',
      createdAt: 'Created At',
      actions: 'Actions',
      searchByName: 'Search by name...',
    },
    profile: {
      allPatients: 'All patients',
      askEluve: 'Ask Eluve',
      sectionsAriaLabel: 'Patient profile sections',
      appointmentsLoadFailed: 'Failed to load appointment data.',
      documentsLoadFailed: 'Failed to load document data.',
      importedNotesHeading: 'Imported notes',
      importedNotesLoadFailed: 'Failed to load imported notes.',
      overviewEmptyDescription:
        "The patient's medical information will live here.",
    },
    rail: {
      editProfile: 'Edit profile',
      moreActions: 'More actions',
      patientInformation: 'Patient information',
      identityDemographics: 'Identity & demographics',
      contactRelationships: 'Contact & relationships',
      status: 'Status',
      dateOfBirth: 'Date of birth',
      dobWithAge: '{date} · {age} yrs',
      mrn: 'MRN',
      primaryCare: 'Primary care',
      lastVisit: 'Last visit',
      nextVisit: 'Next visit',
      legalSex: 'Legal sex',
      genderIdentity: 'Gender identity',
      pronouns: 'Pronouns',
      race: 'Race',
      ethnicity: 'Ethnicity',
      preferredLanguage: 'Preferred language',
      maritalStatus: 'Marital status',
      email: 'Email',
      address: 'Address',
      emergencyContact: 'Emergency contact',
      phone: 'Phone',
      phoneMobile: 'Mobile',
      phoneHome: 'Home',
      phoneWork: 'Work',
      statusActive: 'Active',
      statusDeceased: 'Deceased',
      statusLeftPractice: 'Left practice',
      markDeceased: 'Mark as deceased…',
      removeDeceased: 'Remove deceased status…',
      markLeftPractice: 'Mark as left the practice…',
      markActive: 'Mark as active…',
      recordUnavailable: 'Patient record unavailable or restricted.',
      recordLoadError: "Couldn't load the patient record.",
    },
    editProfile: {
      description:
        "Update {name}'s identity, demographics, and contact details.",
      thisPatient: 'this patient',
      updated: 'Profile updated.',
      conflict:
        'This profile changed since you opened it. Close the dialog and reopen it to edit the latest version.',
      saveFailed: "Couldn't save: {message}",
      unknownError: 'unknown error',
      discardTitle: 'Discard unsaved changes?',
      discardDescription:
        "Your edits to this profile haven't been saved and will be lost if you close now.",
      discardChanges: 'Discard changes',
    },
    status: {
      deceasedSetTitle: 'Mark {name} as deceased?',
      deceasedSetDescription:
        'Their profile will show a deceased status. This can be undone later.',
      deceasedSetConfirm: 'Mark as deceased',
      deceasedClearTitle: 'Remove the deceased status for {name}?',
      deceasedClearDescription:
        'Their profile will no longer show a deceased status.',
      deceasedClearConfirm: 'Remove status',
      leftSetTitle: 'Mark {name} as having left the practice?',
      leftSetDescription:
        "Their profile will show that they've left the practice. This can be undone later.",
      leftSetConfirm: 'Mark as left',
      leftClearTitle: 'Mark {name} as active?',
      leftClearDescription:
        "Their profile will no longer show that they've left the practice.",
      leftClearConfirm: 'Mark as active',
      updated: 'Patient status updated.',
      changedElsewhere:
        "This patient's status was just changed elsewhere. Refreshing.",
      updateFailed: 'Patient status could not be updated.',
    },
    medicalProfile: {
      accessEluveAdminLabel: 'Full access',
      accessEluveAdminDetail:
        "As an Eluve admin, you can see every patient's FHIR record across every organization.",
      accessTenantAdminLabel: 'Full access in this organization',
      accessTenantAdminDetail:
        "As an organization admin, you can see every patient's FHIR record in this organization.",
      accessTenantMemberLabel: "You're a general practitioner for this patient",
      accessTenantMemberDetail:
        "That's why you can see this record. You'll only see FHIR records for patients where you're listed as the GP.",
      noFhirPatientTitle: 'No FHIR Patient visible',
      noFhirPatientDescription:
        'Either this patient has not been synced into the FHIR store yet, or your access policy filters them out.',
    },
    careTeam: {
      unnamedPractitioner: 'Unnamed practitioner',
      generalPractitioner: 'General Practitioner',
      name: 'Name',
      role: 'Role',
      contact: 'Contact',
      reference: 'Reference',
      history: 'History',
      assignmentHistory: 'Assignment history',
      assignmentHistoryDescription:
        "Every change to this patient's general practitioners, pulled from the FHIR resource history.",
      noHistory: 'No history available.',
      when: 'When',
      change: 'Change',
      gps: 'GPs',
      version: 'Version',
      unknownTime: 'Unknown time',
      noGpChanges: 'No GP changes',
      initialVersion: 'Initial version',
      noMembers: 'No care-team members assigned to this patient.',
    },
    observations: {
      showTrends: 'Show trends',
      loadingTrends: 'Loading trends…',
      noneToChart:
        'No observations to chart yet. Extract a lab report from the Patient Uploads tab first.',
      analyte: 'Analyte',
      undated: 'Undated',
      reportFallback: 'Report',
    },
    extracted: {
      loading: 'Loading extracted observations…',
      empty:
        'No agent-extracted observations yet. Upload a document and run the extraction agent from the Patient Uploads tab.',
      unnamedObservation: 'Unnamed observation',
      laboratoryReport: 'Laboratory report',
      labReportKind: 'Lab report',
      extractedRecordKind: 'Extracted record',
      reportDate: 'Report {date}',
      extractedDate: 'Extracted {date}',
      observationCountOne: '{count} observation',
      observationCountMany: '{count} observations',
      needsReview: 'Needs review',
      approved: 'Approved',
      review: 'Review',
      edit: 'Edit',
      reject: 'Reject',
      approve: 'Approve',
      saveAndApprove: 'Save & approve',
      collapseReport: 'Collapse report',
      expandReport: 'Expand report',
      observationName: 'Observation name',
      valuePlaceholder: 'value',
      unitPlaceholder: 'unit',
      referenceRange: 'Reference range',
      refPrefix: 'Ref: {range}',
      approvedToastOne: 'Approved {title} ({count} observation)',
      approvedToastMany: 'Approved {title} ({count} observations)',
      rejectedToast: 'Rejected {title}',
      approvalFailed: 'Approval failed: {error}',
      rejectionFailed: 'Rejection failed: {error}',
      observationDetails: 'Observation details',
      provenance: 'Provenance',
      chainOfEffects: 'Chain of effects',
      onBehalfOf: 'On behalf of',
      agent: 'Agent',
      permissions: 'Permissions',
      issued: 'Issued',
      source: 'Source',
      observation: 'Observation',
      byPublisher: 'by {publisher}',
      agentActor: '{agent} (agent)',
      unknownActor: 'unknown',
    },
    files: {
      untitled: 'Untitled',
      loadFailed: 'Failed to load files',
      uploaded: 'Uploaded {name}',
      uploadFailed: 'Failed to upload {name}',
      extractedPartialOne:
        'Extracted {extracted} of {total} lab results — {dropped} could not be structured and was omitted. Review them under Medical Profile → Observations.',
      extractedPartialMany:
        'Extracted {extracted} of {total} lab results — {dropped} could not be structured and were omitted. Review them under Medical Profile → Observations.',
      extractedSuccessOne:
        'Extracted {count} lab result. Review them under Medical Profile → Observations.',
      extractedSuccessMany:
        'Extracted {count} lab results. Review them under Medical Profile → Observations.',
      notLabReport: "This document isn't a lab report — nothing was extracted.",
      noLabResults: 'No lab results were found in this document.',
      reextractionDisabled:
        'This report was already reviewed and finalized; re-extraction is disabled.',
      extractionAlreadyRunning:
        'An extraction for this document is already running. Please wait for it to finish.',
      extractionFailedWithError: 'Extraction failed: {error}',
      extractionFailed: 'Extraction failed',
      installFailedWithError: 'Install failed: {error}',
      installFailed: 'Install failed',
      noDownloadUrl: 'No download URL for this file.',
      openFailed: 'Failed to open {name}',
      name: 'Name',
      type: 'Type',
      size: 'Size',
      uploadedColumn: 'Uploaded',
      status: 'Status',
      notExtracted: 'Not extracted',
      viewDetails: 'View Details',
      extract: 'Extract',
      uploadFile: 'Upload file',
    },
    consent: {
      aiAgent: 'AI agent',
      unknownPublisher: 'Unknown publisher',
      allowTitle: 'Allow {name}?',
      builtByEluve: 'Built by Eluve',
      builtBy: 'Built by {publisher}',
      thirdPartyApp: 'Third-party app',
      description:
        'This agent runs on your behalf and is restricted to only the actions listed below. It can never read or write anything else.',
      agentCan: 'This agent can:',
      allow: 'Allow',
      publisherLogoAlt: '{publisher} logo',
    },
    reviewDoc: {
      title: 'Review extracted results',
      loadingDocument: 'Loading document…',
      displayError: "Couldn't display this document.",
      documentFallback: 'Document',
    },
    carePlans: {
      plan: 'Plan',
      status: 'Status',
      started: 'Started',
      noFhirForList:
        'No FHIR record is visible for this patient, so no care plans can be shown.',
      loading: 'Loading care plans…',
      loadError: "Something went wrong loading this patient's care plans.",
      empty: 'No care plans for this patient.',
      backToList: '← Back to care plans',
      noFhirForDetail:
        'No FHIR record is visible for this patient, so this care plan cannot be shown.',
      loadingOne: 'Loading care plan…',
      detailLoadError: 'This care plan could not be loaded.',
      unknownStart: 'Unknown start',
      ongoing: 'ongoing',
      protocol: 'Protocol',
      conditions: 'Conditions',
      activities: 'Activities',
      noActivities: 'No activities recorded on this care plan.',
    },
    documents: {
      generatingPdf: 'Generating PDF...',
      pdfPreviewFailedToast: 'Failed to generate PDF preview',
      pdfPreviewFailedMessage: 'Failed to generate PDF preview.',
      titleEmpty: 'Title cannot be empty.',
      contentEmpty: 'Document content cannot be empty.',
      saveFailed: 'Failed to save changes',
      documentTitle: 'Document title',
      markdownTab: 'Markdown',
      pdfTab: 'PDF',
      discardTitle: 'Discard unsaved edits?',
      discardDescription: 'Your changes will be lost.',
      discard: 'Discard',
      updated: 'Document updated',
      title: 'Title',
      type: 'Type',
      date: 'Date',
    },
    conditions: {
      header: 'Conditions',
    },
    labResults: {
      header: 'Lab Results',
      name: 'Name',
      value: 'Value',
      referenceRange: 'Reference Range',
      observationsCount: 'Observations Count',
      outsideReferenceRange: 'Outside reference range',
      issuedAt: 'Issued At',
    },
    overview: {
      reasonForVisit: 'Reason for visit',
      sourceFallback: 'Source',
      situation: 'Situation',
    },
  },
  appointmentTools: {
    common: {
      close: 'Close',
      tryAgain: 'Try again',
      searching: 'Searching...',
      noResultsFor: 'No results for "{query}"',
      loading: 'Loading...',
      generatePdfFailed: 'Failed to generate PDF',
      preparePdfFailed: 'Failed to prepare PDF',
      acceptRecommendationFailed: 'Failed to accept recommendation',
    },
    regenerate: {
      noModelsFound: 'No models found',
      defaultFromTemplate: 'Default (from template)',
    },
    citations: {
      unknownTest: 'Unknown Test',
      normal: 'Normal',
      abnormal: 'Abnormal',
      labResults: 'Lab Results',
      testsCount: '{count} tests',
      abnormalCount: '{count} abnormal',
      normalCount: '{count} normal',
      abnormalResults: 'ABNORMAL RESULTS',
      normalResults: 'NORMAL RESULTS',
      refRange: '(Ref: {range})',
      result: 'RESULT',
      referenceRange: 'REFERENCE RANGE',
      valueOutsideRange: 'Value is outside reference range',
      unknownMedication: 'Unknown Medication',
      dosageInstructions: 'DOSAGE INSTRUCTIONS',
      intent: 'INTENT',
      priority: 'PRIORITY',
      unknownCondition: 'Unknown Condition',
      codeLabel: 'Code: {code}',
      clinicalStatus: 'CLINICAL STATUS',
      verification: 'VERIFICATION',
      category: 'CATEGORY',
      note: 'NOTE',
      patientFallback: 'Patient',
      dateOfBirth: 'Date of Birth',
      sex: 'Sex',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      unknownObservation: 'Unknown Observation',
      value: 'VALUE',
      carePlan: 'Care Plan',
      description: 'DESCRIPTION',
      goals: 'GOALS',
      activities: 'ACTIVITIES',
      clinicalNote: 'Clinical Note',
      appointmentLabel: 'Appointment: {id}',
      noContent: 'No content available',
      uploadedFile: 'Uploaded File',
      pageSingle: 'Page {page}',
      pageRange: 'Pages {start}-{end}',
      extractedText: 'EXTRACTED TEXT',
      intakeForm: 'Intake Form',
      patientLabel: 'Patient: {name}',
      noFormData: 'No form data available',
      noData: 'No data available',
      sourceFallback: 'Source',
      unnamedArtifact: 'Unnamed Artifact',
      loadingArtifact: 'Loading artifact...',
      errorLoadingArtifacts: 'Error loading artifacts: {message}',
      sourceNotFound:
        'Could not find the source document containing this citation.',
      fetchedArtifacts: 'Fetched {count} artifact(s).',
      sources: 'Sources:',
    },
    documents: {
      createTemplateTitle: 'Create template',
      editTemplateTitle: 'Edit template',
      createDocumentTitle: 'Create a Document or Letter',
      createDocumentSubtitle:
        'Start from one of your templates, or create a new one. Eluve populates it from this note — store it, email it or print it.',
      generateWithAi: 'Generate with AI',
      generateWithAiHint:
        'Describe the document you want — for example, “a referral letter to a cardiologist noting the patient’s chest pain and history.”',
      promptPlaceholder: 'Describe the document…',
      generating: 'Generating…',
      generate: 'Generate',
      templates: 'Templates',
      searchTemplates: 'Search templates…',
      createNewTemplate: 'Create a new template',
      createNewTemplateHint:
        'Describe it with AI to generate documents from your notes',
      noTemplates: 'No templates available',
      createdByYou: 'Created by you',
      createdByEluve: 'Created by Eluve',
      prebuilt: 'Prebuilt',
      fillPdfTitle: 'Fill in a PDF',
      fillPdfSubtitle:
        'Pick a fillable PDF template or upload a new one. Eluve fills the fields from this note.',
      createPdfTemplate: 'Create a new PDF form template',
      createPdfTemplateHint:
        'Drop in any static PDF — Eluve detects or adds the fillable fields',
      noPdfTemplates: 'No PDF templates yet',
      yourPdfTemplates: 'Your PDF templates',
      eluveTemplates: 'Eluve templates',
      dropPdfHere: 'Drop your PDF here',
      clickToBrowse: 'Click to browse, or drag and drop your PDF here',
      pdfOnlyMax: 'PDF only, max 10 MB',
      enterTemplateName: 'Please enter a template name',
      enterTemplateContent: 'Please enter template content',
      templateUpdated: 'Template updated',
      templateCreated: 'Template created',
      updateTemplateFailed: 'Failed to update template',
      createTemplateFailed: 'Failed to create template',
      onlyPdfAllowed: 'Only PDF files are allowed',
      fileTooLarge: 'File size must be less than 10 MB',
      unableToDetermineTenant: 'Unable to determine tenant',
      somethingWentWrong: 'Something went wrong',
      templateGenerationFailed: 'Template generation failed: {message}',
      backToTemplates: 'Back to templates',
      createTemplateSubtitle:
        'Generate it from an AI prompt — describe the document this template should produce. For example, “a referral letter to a cardiologist noting the patient’s chest pain and history.”',
      renamePdfTemplate: 'Rename this PDF form template.',
      updateTemplateContent: 'Update your template content and name.',
      and: 'AND',
      addExample: 'Add an example to start from (optional)',
      addExampleHint:
        "Upload an example document — Eluve turns it into template content and adds it to what you've written.",
      uploadingFile: 'Uploading {fileName}...',
      fileFallback: 'file',
      generatingFromDocument: 'Generating template from document...',
      templateName: 'Template name',
      required: 'Required',
      namePlaceholder: 'Name your template',
      saving: 'Saving...',
      save: 'Save',
      saveTemplateFailed: 'Failed to save template',
      readingLayout: 'Eluve is reading the layout and adding form fields…',
      detectingLabels: 'Detecting labels, checkboxes and signature areas',
      tryAnotherFile: 'Try another file',
      back: 'Back',
      createPdfTemplateTitle: 'Create a PDF form template',
      createPdfTemplateSubtitle:
        'Upload a static PDF. Eluve checks for fillable fields and adds them with AI if needed.',
      fieldsCountOne: '{count} field',
      fieldsCountMany: '{count} fields',
      formFieldsCountOne: '{count} form field',
      formFieldsCountMany: '{count} form fields',
      alreadyFillableForm: '{fileName} is already a fillable form · {fields}',
      addedFields:
        'Eluve added {fields} to {fileName}. Review, then save as a template.',
      detectedFields: 'DETECTED FIELDS · {count}',
      saveAsTemplate: 'Save as template',
      templateSaved: 'Template saved',
      templateSavedHint:
        '"{name}" is now in your PDF templates. Fill it from this note now, or any time later.',
      done: 'Done',
      fillFromNote: 'Fill from this note →',
      fillableForm: 'Fillable form',
      use: 'Use',
      templateOptions: 'Template options',
      edit: 'Edit',
      delete: 'Delete',
      deleteTemplateTitle: 'Delete this template?',
      deleteTemplateDescription:
        'The {name} template will be permanently deleted.',
      deleting: 'Deleting...',
      createDocument: 'Create Document',
      create: 'Create',
      fillPdfMenuHint: 'Eluve fills a fillable PDF from this note',
      createDocOrLetter: 'Create a document or letter',
      createDocOrLetterHint: 'Generate prose to email, store or print',
      templateDeleted: 'Template deleted',
      deleteTemplateFailed: 'Failed to delete template',
      generateError: 'Something went wrong generating this document.',
      preparingDocument: 'Preparing your document',
      docFromSession: 'This document will be generated from your session data.',
      switchTabsHint:
        "You can switch tabs in the meantime — we'll keep working in the background.",
      stopGenerating: 'Stop generating',
      saveChangesFailed: 'Failed to save changes',
      copiedToClipboard: 'Copied to clipboard',
      copyFailed: 'Failed to copy',
      pdfDownloaded: 'PDF downloaded',
      emailClipboardFallback:
        'Document copied to clipboard — paste into your email',
      copied: 'Copied',
      copy: 'Copy',
      export: 'Export',
      downloadPdf: 'Download PDF',
      emailDocument: 'Email Document',
      pdfLoadFailed: "Couldn't load this PDF.",
      preparingPdf: 'Preparing your PDF',
      pdfFromSession: 'This PDF form will be filled using your session data.',
      preparingPdfButton: 'Preparing PDF…',
      fillError: 'Something went wrong filling this form.',
      prevPage: '← Prev',
      nextPage: 'Next →',
      pageOf: 'Page {page} of {total}',
      previewFailed: "Couldn't preview this PDF.",
      download: 'Download',
      moreFields: '+{count} more',
      skippedFieldsOne:
        "{count} field couldn't be filled automatically — review before using: {fields}",
      skippedFieldsMany:
        "{count} fields couldn't be filled automatically — review before using: {fields}",
      alteredFieldsOne:
        '{count} field was adjusted to fit the form (unsupported characters or length) — review: {fields}',
      alteredFieldsMany:
        '{count} fields were adjusted to fit the form (unsupported characters or length) — review: {fields}',
      appearanceFallback:
        'This PDF saved with compatibility fallback — some viewers may render fields differently. Review before sending.',
      humanOnlyFieldsOne:
        '{count} field intentionally left for manual completion (signatures, consent, etc.)',
      humanOnlyFieldsMany:
        '{count} fields intentionally left for manual completion (signatures, consent, etc.)',
    },
    tabs: {
      note: 'Note',
      carePlan: 'Care Plan',
      deleteDocAria: 'Delete {title}',
      deleteDocumentTitle: 'Delete document?',
      deleteDocumentDescription:
        'This will permanently delete “{title}.” This action cannot be undone.',
    },
    billing: {
      addBillingCode: 'Add Billing Code',
      updatePriceFailed: 'We could not update the price.',
      revertChanges: 'Revert changes',
      removeCode: 'Remove code',
      removeBillingCodeFailed: 'We could not remove the billing code.',
      addBillingCodeFailed: 'We could not add the billing code.',
      noBillingCodesAdded: 'No billing codes added yet.',
      modifiersHint: 'A comma separated list of modifiers.',
      editModifiers: 'Edit modifiers',
      updateModifiers: 'Update Modifiers',
      editPrice: 'Edit price',
      updatePrice: 'Update Price',
      billingDetails: 'Billing Details',
      noIcdCodes: 'No ICD codes added above yet.',
      updateSuperbill: 'Update Superbill',
      generateSuperbill: 'Generate Superbill',
      billingCodesHeading: 'Billing Codes',
      cptCopyrightLine1: 'CPT copyright 2025 American Medical Association.',
      cptCopyrightLine2: 'All rights reserved.',
      addAmendments: 'Add Amendments',
      noBillingCodes: 'No billing codes.',
      recommendedCodes: 'Recommended Codes',
      generateRecommendedCodes: 'Generate Recommended Codes',
      generatingRecommendations: 'Generating recommendations...',
      generateRecommendationsFailed:
        'Error generating billing code recommendations. Please try again later.',
      addAmendmentFailed: 'Failed to add billing code amendment',
      billingCodesFeedback: 'Were these billing codes helpful?',
      deleteAmendmentFailed: 'Failed to delete billing code amendment',
      updateModifiersFailed: 'We could not update the modifiers.',
      updateQuantityFailed: 'We could not update the quantity.',
      selectIcdFailed: 'We could not select the ICD code(s).',
      deselectIcdFailed: 'We could not deselect the ICD code(s).',
      removeCodeFailed: 'Failed to remove billing code',
      addCodeFailed: 'Failed to add billing code',
      codeToRemoveNotFound: 'Could not find the billing code to remove',
      searchBillingCodes: 'Search billing codes',
      chooseCodePlaceholder: 'Choose a code you would like to add',
      enterCodeOrSearchTerm: 'Enter code or search term',
      chooseDiagnosis: 'Choose a diagnosis',
      searchDiagnosis: 'Search diagnosis',
      superbill: 'Superbill',
    },
    medicine: {
      orderDetails: 'Order Details',
      doseLabel: 'Dose *',
      dosePlaceholder: 'e.g., 10 mg',
      frequencyLabel: 'Frequency *',
      selectFrequency: 'Select frequency',
      routeLabel: 'Route *',
      selectRoute: 'Select route',
      durationLabel: 'Duration',
      durationPlaceholder: 'e.g., 30 days',
      quantityLabel: 'Quantity to Dispense *',
      refillsLabel: 'Refills Allowed',
      additionalInstructions: 'Additional Instructions',
      instructionsPlaceholder: 'e.g., Take with food, avoid alcohol...',
      updateOrder: 'Update Order',
      addToOrders: 'Add to Orders',
      medicationOrders: 'Medication Orders',
      searchMedications: 'Search medications...',
      noMedicationsFound: 'No medications found for "{query}"',
      newOrder: 'New Order: {name}',
      currentOrders: 'Current Orders',
      takeInstruction: 'Take {dose} {route}, {frequency}',
      viewPdf: 'View PDF',
      sendToPharmacy: 'Send to Pharmacy',
      orderSentAt: 'Order sent to pharmacy at {time}',
      ordersSentSuccess: 'Orders sent to pharmacy successfully',
      quickActions: 'Quick Actions',
      emptyOrders:
        'No medication orders yet. Search for medications or review AI recommendations above.',
      addOrderFailed: 'Failed to add medication order',
      removeMedicationFailed: 'Failed to remove medication',
      updateOrderFailed: 'Failed to update medication order',
      recommendationsFeedback: 'Were these medication recommendations helpful?',
      removeMedicineFailed: 'Failed to remove medicine',
      medication: 'Medication',
      recommendedMedication: 'Recommended Medication',
      medicationsFeedback: 'Were these medications helpful?',
      typeMedicineName: 'Type medicine name and press Enter to search',
      noMedicinesFound: 'No medicines found',
      chooseMedicine: 'Choose medicine you would like to add',
      searchMedicineInventory: 'Search medicine inventory',
    },
    referrals: {
      referralSent: 'Referral sent to {name}',
      referTo: 'Refer to {name}',
      specialistInformation: 'Specialist Information',
      referralLetter: 'Referral Letter',
      printPdf: 'Print PDF',
      sendReferral: 'Send Referral',
      referToSpecialist: 'Refer to Specialist',
      searchSpecialists: 'Search specialists by name or specialty...',
      moreResults: '{count} more results — refine your search',
      noSpecialistsFound: 'No specialists found for "{query}"',
    },
    outputs: {
      billingCodeRecommendations: 'Billing Code Recommendations',
      editDiagnosis: 'Edit this diagnosis',
      removeDiagnosis: 'Remove this diagnosis',
      justificationLabel: 'Justification:',
      excerptLabel: 'Excerpt:',
      urgencyHigh: 'Urgency: High',
      supportingEvidence: 'SUPPORTING EVIDENCE',
      differentialGap: 'DIFFERENTIAL GAP',
      keyUncertainty: 'KEY UNCERTAINTY',
      suggestedNextSteps: 'SUGGESTED NEXT STEPS',
      sourcesCaption: 'SOURCES',
      insightHelpful: 'Was this insight helpful?',
      analyzingDiagnosis: 'Analyzing diagnosis...',
      underConsideration: 'Under Consideration',
      mostLikely: 'MOST LIKELY',
      moderateSignal: 'MODERATE SIGNAL',
      weakSignal: 'WEAK SIGNAL',
      generateOutputFailed: 'Error generating output. Please try again later.',
      noOutputAvailable: 'No Output Available',
      outputProblem: 'There was a problem generating this output',
      generatingOutput: 'Generating Output',
      pfshTitle: 'Past, Family and Social History (PFSH)',
      allergies: 'Allergies',
      medications: 'Medications',
      existingConditions: 'Existing Conditions',
      surgeries: 'Surgeries',
      socialHistory: 'Social History',
      familyHistory: 'Family History',
    },
    templateSelector: {
      loadingTemplates: 'Loading Templates...',
      defaultBundle: 'Default - {name}',
      selectTemplate: 'Select Template',
      updateBundleFailed: 'Unable to update template bundle',
      templateLabel: 'Template',
      defaultForOrg: 'Default for your organization',
    },
    sessionType: {
      updateFailed: 'Failed to update session type',
      noneAvailable: 'None available',
      noType: 'No Type',
      searchTypes: 'Search types...',
    },
    page: {
      loadingAppointment: 'Loading Appointment...',
      canceledInEhr: 'This appointment was canceled in your EHR.',
      viewExternalChart: 'View External Chart',
    },
    transcription: {
      analyzingAudio: 'Analyzing audio',
      analyzingAudioDescription:
        'Analyzing audio to improve accuracy. ({count}) files remain.',
    },
    chartsExport: {
      downloadFailedOne: "{count} appointment couldn't be downloaded",
      downloadFailedMany: "{count} appointments couldn't be downloaded",
      notExportedOne:
        'The following {count} appointment could not be exported:',
      notExportedMany:
        'The following {count} appointments could not be exported:',
      cantExportDefault:
        "The appointments you selected can't be exported. Make sure they have available summaries.",
      cantExportTitle: "Appointments can't be exported to PDF",
      preparingDownload: 'Preparing download',
      downloadingChartsCount: 'Downloading {count} charts',
      downloadingCharts: 'Downloading charts',
      downloadComplete: 'Download complete',
      chartsDownloadedOne: '{count} chart downloaded',
      chartsDownloadedMany: '{count} charts downloaded',
      downloadChartsAsPdf: 'Download Charts as PDF',
      noSummariesTooltip:
        "Selected appointments don't have available summaries",
    },
    appointmentsTable: {
      selectAllAria: 'Select all',
      selectRowAria: 'Select row',
      locationFilter: 'Location',
      statusFilter: 'Status',
      userFilter: 'User',
      startDateFilter: 'Start Date',
      youSuffix: '{name} (You)',
      selectAllPrefix: 'All',
      selectAllSuffix:
        'appointments on this page are selected. Select all {count} filtered appointments?',
      allFilteredSelectedSuffix: 'filtered appointments are selected.',
      selectAll: 'Select All',
      clearSelection: 'Clear Selection',
      selectedCount: '{count} selected',
      onlyMyAppointments: 'Only my appointments',
    },
  },
  outputTemplates: {
    common: {
      templates: 'Templates',
      createTemplate: 'Create Template',
      noCreatePermission:
        "You do not have permission to create templates. Please contact your organization's administrator.",
      backToTemplates: 'Back to templates',
      saving: 'Saving…',
      saveDraft: 'Save Draft',
      nameRequired: 'Give the template a name first',
      draftSaved: 'Draft saved',
      somethingWentWrong: 'Something went wrong',
      generationFailed: 'Template generation failed: {message}',
      generating: 'Generating…',
      generate: 'Generate',
      preview: 'Preview',
      templateCreated: 'Template created',
      createTemplateFailed: 'Failed to create template',
    },
    generateWithAi: {
      title: 'Generate with AI',
      intro:
        'Tell us about your practice and we’ll design a template around how you work.',
      noteFormatLabel: 'Note format',
      noteLengthLabel: 'Note length',
      specialtyLabel: 'Specialty',
      visitTypeLabel: 'Visit type (optional)',
      instructionsLabel: 'Additional instructions (optional)',
      specialtyPlaceholder: 'Select or type your specialty',
      visitTypePlaceholder: 'Select or type a visit type',
      instructionsPlaceholder:
        'Anything that should shape the note — sections you always include, tone, payer requirements…',
      popularKeywords: 'Popular keywords',
      designing: 'Designing your template…',
      searchOrType: 'Search or type your own…',
      useTypedValue: 'Use “{value}”',
      failedToGenerate: 'Failed to generate the template',
      unableToGenerate: 'Unable to generate a template from this request.',
      formatSoapExpansion: 'Subjective · Objective · Assessment · Plan',
      formatSoapDescription:
        'The standard clinical note for most medical visits.',
      formatBirpExpansion: 'Behavior · Intervention · Response · Plan',
      formatBirpDescription:
        'Centers on observed behavior and your interventions — common in behavioral health.',
      formatDapExpansion: 'Data · Assessment · Plan',
      formatDapDescription:
        'A streamlined note that folds everything observed into one data section.',
      formatGirpExpansion: 'Goal · Intervention · Response · Plan',
      formatGirpDescription:
        'Leads with treatment-plan goals — fits goal-driven therapy programs.',
      lengthConciseLabel: 'Concise',
      lengthConciseDescription:
        'Core sections only, with brief guidance in each.',
      lengthBalancedLabel: 'Balanced',
      lengthBalancedDescription:
        'Core sections with moderate structure inside each.',
      lengthDetailedLabel: 'Detailed',
      lengthDetailedDescription:
        'Core sections with thorough, specialty-specific guidance in each.',
    },
    workspace: {
      previewEmptyState: 'See what a note from this template would look like',
      generatePreview: 'Generate Preview',
      templateNameLabel: 'Template Name',
      templateNamePlaceholder: 'e.g. Internal Medicine SOAP Note',
      addSection: 'Add section',
      addGroup: 'Add group',
      additionalSettings: 'Additional settings',
      templateDescriptionLabel: 'Template description',
      templateDescriptionPlaceholder:
        'Describe when this template should be used…',
      allowGroups: 'Allow groups',
      allowGroupsDescription:
        'Group related sections under a shared name. A group reorders as one unit, and sections can be dragged in and out of it.',
      customTranscriptOption: 'Custom transcript…',
      editCustomTranscript: 'Edit custom transcript',
      generatePreviewTooltip: 'Generate preview',
      regeneratePreviewTooltip: 'Regenerate preview',
      generatePreviewFailed: 'Failed to generate preview',
      customTranscriptTitle: 'Custom transcript',
      customTranscriptDescription:
        'Paste a visit transcript to test this template with.',
      customTranscriptPlaceholder: 'Paste the transcript here…',
      useTranscript: 'Use transcript',
      modelLabel: 'Model',
      generatedIn: 'Generated in {duration}',
      tokenBreakdown: '{input} input · {output} output',
      tokenCount: '{count} tokens',
      editSection: 'Edit section',
    },
    wizard: {
      title: 'Create a template',
      subtitle: 'How do you want to start?',
      aiModeDescription:
        "Describe the note you want and we'll draft the template.",
      uploadModeTitle: 'Upload or import an existing note',
      uploadModeDescription:
        'Upload or paste a past note and we’ll match its structure.',
      manualModeTitle: 'Start from scratch',
      manualModeDescription: 'Build your template section by section.',
      orStartFromScratch: 'or start from scratch',
      back: 'Back',
      uploadStepDescription:
        'Upload a past note — or paste one in — and we’ll match its structure to build your template.',
      onlyPdfSupported: 'Only PDF files are supported.',
      fileTooLarge: 'File size must be less than 10 MB.',
      prepareUploadFailed: 'Failed to prepare file upload',
      uploadFailed: 'Failed to upload file',
      failedToGenerateFromNote: 'Failed to generate a template from this note',
      unableToGenerateFromNote: 'Unable to generate a template from this note.',
      uploadingFile: 'Uploading {fileName}…',
      fileFallback: 'file',
      analyzingNote: 'Analyzing your note and drafting the template…',
      dropZoneHint: 'Drag & drop a PDF here, or click to browse',
      dropZoneLimit: 'PDF only, max 10 MB',
      removeFile: 'Remove file',
      orDivider: 'OR',
      pasteNotePlaceholder: 'Paste the full text of an existing note…',
      removeFileToPaste: 'Remove the uploaded file to paste a note instead.',
    },
    edit: {
      title: 'Edit Template',
      disabledForOrg: 'Disabled for the organization',
      enabledForOrg: 'Enabled for the organization',
      updateAvailabilityFailed: 'Failed to update availability',
      versionPublished: 'Version published',
      newerVersionActive: 'A newer version is already active',
      saveFailed: 'Failed to save',
      savedCopy: 'Saved a copy',
      saveCopyFailed: 'Failed to save a copy',
      copyOf: 'Copy of {name}',
      untitled: 'Untitled',
      versionNowActive: 'Version {number} is now active',
      setActiveVersionFailed: 'Failed to set active version',
      moreActions: 'More actions',
      setAsActive: 'Set as Active',
      enabledForOrganizationItem: 'Enabled for Organization',
      saveACopy: 'Save a Copy',
      templateVersionAria: 'Template version',
      versionItemActive: 'Version {number} · Active',
      versionItemDraft: 'Version {number} · Draft',
      versionItemInactive: 'Version {number} · Inactive',
      publishVersion: 'Publish Version',
      editDraft: 'Edit Draft',
      creating: 'Creating…',
      createDraft: 'Create Draft',
      templateNotFound: 'Template not found.',
      unsupportedBlocks:
        "This template uses advanced blocks that this editor can't represent yet, so saving is disabled — re-saving here would drop those blocks.",
      readOnlyEluvePrefix:
        "This template is provided by Eluve and can't be changed here. Feel free to explore and tweak it — use ",
      readOnlyMemberPrefix:
        "This template is owned by another member, so it can't be changed here. Use ",
      saveACopyInline: 'Save a copy',
      readOnlySuffix:
        ' to create your own editable version for this organization.',
      adminEditingOthers:
        "You're editing a template owned by another member. As an admin you can publish changes — it stays owned by them.",
    },
    new: {
      title: 'New Template',
      saveDraft: 'Save draft',
      saveTemplate: 'Save template',
      templateSaved: 'Template saved',
      saveFailed: 'Failed to save template',
    },
    details: {
      setDefaultFailed: 'Variant could not be set as the default.',
      updateFailed: 'Template could not be updated.',
      updateNotesFailed: 'Variant notes could not be updated.',
      variantJsonCopied: 'Variant JSON copied to clipboard.',
      variantJsonCopyFailed: 'Variant JSON could not be copied.',
      jsonSchemaCopied: 'JSON Schema copied to clipboard.',
      jsonSchemaCopyFailed: 'JSON Schema could not be copied.',
      templateNamePlaceholder: 'Template name',
      templateDescriptionPlaceholder: 'Template description',
      variants: 'Variants',
      export: 'Export',
      variantJson: 'Variant JSON',
      viewJsonSchema: 'View JSON Schema',
      jsonSchemaTitle: 'Output Template JSON Schema',
      variantNotes: 'Variant Notes',
      noNotes: 'No notes.',
    },
    bundlesList: {
      draftTooltip: 'You have an unpublished draft of this template.',
      draft: 'Draft',
      readOnlyTooltip: "Provided by Eluve — this template can't be edited.",
      readOnly: 'Read only',
      disabledTooltip:
        "This template is turned off for your organization, so it won't be available when generating notes.",
      disabled: 'Disabled',
      noDescription: 'No description yet.',
      searchPlaceholder: 'Search templates…',
      showOnlyMine: "Show only my organization's templates",
      emptyState: 'No templates yet — create your first one.',
      noMatches: 'No templates match your filters.',
    },
    legacyList: {
      nameHeader: 'Name',
      sourceHeader: 'Source',
      descriptionHeader: 'Description',
      createdAtHeader: 'Created At',
      statusHeader: 'Status',
      variantCountHeader: '# Variant',
      draft: 'Draft',
      submitted: 'Submitted',
    },
    generateDialog: {
      title: 'Generate Template with AI',
      description:
        'Generate a new medical document template based on your requirements.',
      documentDescriptionRequired: 'Document description is required',
      documentDescriptionLabel: 'Document Description',
      requiredIndicator: '(Required)',
      documentDescriptionPlaceholder:
        'Describe the medical document format, including sections, fields, and structure requirements...',
      typeLabel: 'Type',
      typePlaceholder:
        'e.g., "clinical note", "lab report", "discharge summary"',
      requiredFieldsLabel: 'Required Fields',
      requiredFieldsPlaceholder:
        'Comma-separated list: chief complaint, assessment, plan',
      constraintsLabel: 'Additional Instructions',
      constraintsPlaceholder:
        'Any specific validation rules, formatting requirements, or constraints...',
      exampleDocumentLabel: 'Example Document (PDF)',
      beta: 'Beta',
      pdfDocumentAria: 'PDF document',
      pdfUploaded: 'PDF uploaded successfully',
      uploadingPdf: 'Uploading PDF...',
      generateTemplate: 'Generate Template',
      onlyPdfAllowed: 'Only PDF files are allowed',
      fileTooLarge: 'File size must be less than 10MB',
      invalidExtension: 'Invalid file extension',
      uploadPdfFailed: 'Failed to upload PDF file',
      pdfUploadSuccess: 'PDF file uploaded successfully',
      generatedSuccess: 'Output template generated successfully!',
      generateFailed: 'Failed to generate template',
      generateOutputFailed: 'Failed to generate output template',
    },
    prefill: {
      title: 'Pre-fill your template',
      importJson: 'Import JSON',
      pastePlaceholder: 'Paste exported JSON here',
      import: 'Import',
      invalidJsonStructure:
        'Invalid JSON format. Expected exported template structure.',
      importSuccess: 'JSON Schema imported successfully',
      invalidJson: 'Invalid JSON format. Please check your input.',
    },
    preview: {
      promptTemplateRequired: 'Prompt template is required',
      invalidUuid: 'Must be a valid UUID',
      outputLanguageRequired: 'Output language is required',
      noTemplateAvailable: 'No template available for generation',
      generateNoteFailed: 'Failed to generate note',
      provideTranscriptBy: 'To generate a note, provide a transcript by:',
      chooseSampleTranscript: 'Choosing a sample transcript',
      provideAppointmentId: 'Providing an appointment ID',
      appointmentIdLabel: 'Appointment ID',
      transcriptLabel: 'Transcript',
      transcriptPlaceholder: 'Paste a sample transcript',
      orChooseSample: 'Or choose a sample transcript:',
      promptTemplateLabel: 'Prompt Template',
      outputLanguageLabel: 'Output Language',
      regenerateNote: 'Re-generate Note',
      generateNote: 'Generate Note',
      hideDetails: 'Hide details',
      showDetails: 'Show details',
      previewTemplateTab: 'Preview Template',
    },
    variant: {
      title: 'Create Template Variant',
      createFailed: 'Failed to create output template',
    },
    headerButtons: {
      deleteDraft: 'Delete Draft',
      submitTemplate: 'Submit Template',
    },
    unsavedChanges: {
      title: 'Discard unsaved changes?',
      description:
        'You have unsaved changes to this template. If you leave now, they’ll be lost.',
      keepEditing: 'Keep editing',
      discard: 'Discard changes',
    },
    editor: {
      sectionTitlePlaceholder: 'Section title',
      groupNamePlaceholder: 'Group name',
      sectionBodyPlaceholder: 'Describe the expected output for this section…',
      dragToReorderSection: 'Drag to reorder section',
      dragToReorderGroup: 'Drag to reorder group',
      required: 'Required',
    },
    bubbleMenu: {
      generateUsingAi: 'Generate using AI',
      writeManually: 'Write manually',
      ungroup: 'Ungroup',
      deleteSection: 'Delete section',
      deleteGroupAria: 'Delete group and its sections',
      moveSectionUp: 'Move section up',
      moveSectionDown: 'Move section down',
      moveGroupUp: 'Move group up',
      moveGroupDown: 'Move group down',
      heading: 'Heading',
      subheading: 'Subheading',
      normalText: 'Normal text',
      bold: 'Bold',
      italic: 'Italic',
      bulletList: 'Bullet list',
      numberedList: 'Numbered list',
    },
    progress: {
      openingReadingTranscript: 'Reading the transcript…',
      openingIdentifyingSpeakers: 'Identifying who said what…',
      openingGatheringFacts: 'Gathering medically relevant facts…',
      genericCrossCheckingSymptoms:
        'Cross-checking symptoms against the history…',
      genericExtractingMedications: 'Extracting medication names and dosages…',
      genericNotingNegatives: 'Noting pertinent negatives…',
      genericFlaggingFollowUps:
        'Flagging follow-up items mentioned in the visit…',
      genericSeparatingDetails:
        'Separating patient-reported details from clinical findings…',
      genericCheckingAllergies: 'Checking for allergies and contraindications…',
      genericListeningForTiming: 'Listening for timing and duration cues…',
      genericMatchingComplaints:
        'Matching complaints to the relevant sections…',
      genericReconcilingDetails:
        'Reconciling details mentioned more than once…',
      genericFilteringSmallTalk: 'Filtering out small talk…',
      closingApplyingStructure: 'Applying the template structure…',
      closingTidyingFormatting: 'Tidying formatting and wording…',
      closingConsistencyCheck: 'Running a final consistency check…',
      closingAlmostThere: 'Almost there…',
      buildingSection: 'Building "{label}" section…',
      pullingDetails: 'Pulling visit details relevant to {label}…',
      organizingGroup: 'Organizing the {label} sections…',
      listeningForPlaceholder: 'Listening for the {placeholder}…',
      fillingInPlaceholder: 'Filling in the {placeholder}…',
      scanningForPlaceholder: 'Scanning the visit for the {placeholder}…',
    },
  },
  carePlans: {
    visitNumber: 'Visit {number}',
    phaseNumber: 'Phase {number}',
    review: {
      confirmed: 'Confirmed',
      edited: 'Edited',
      rejected: 'Rejected',
      undo: 'Undo',
      edit: 'Edit',
      reject: 'Reject',
      delete: 'Delete',
      save: 'Save',
      saveChanges: 'Save Changes',
      notSet: 'Not set',
      confirmAllCount: 'Confirm all {count}',
      allConfirmed: '✓ All confirmed',
      undoConfirmAllSection: 'Undo confirm all {section}',
      loadFailed: "Couldn't load the care plan — try reloading the page.",
      updateFailed: 'Could not update the care plan.',
      planActive: 'Plan active',
      planActiveSince: 'Plan active since {date}',
    },
    planBlock: {
      title: 'Care Plan',
      addresses: 'Addresses',
      goals: 'Goals',
      todos: 'To-dos',
      counts: '{linked} in plan · {onRecord} on record only',
      countsLinked: '{linked} in plan',
      addedThisVisit: 'Added this visit',
      dockThisVisit: 'This visit',
      addToPlan: 'Add {label} to the plan',
      removeFromPlan: 'Remove {label} from the plan',
      emptyAddresses:
        'Nothing linked — confirmed conditions stay on the record.',
      emptyGoals: 'Nothing linked — confirmed goals stay on the record.',
      emptyTodos: 'Nothing linked — confirmed to-dos stay on the record.',
      target: 'target',
      awaitingReview: 'awaiting review',
      noTargetMeasure: 'no target measure',
      targetTriggerAria: 'Set target measure for {label}',
      targetPickerTitle: 'Target measure',
      targetPickerEmpty: 'No confirmed readings this visit.',
      targetCurrentAwaiting: 'current · awaiting review',
      targetNoneAction: 'No target measure',
    },
    netNew: {
      title: 'New this visit',
      countOne: '{count} new',
      countMany: '{count} new',
      kindCondition: 'Condition',
      kindGoal: 'Goal',
      kindTodo: 'To-do',
      kindMeasure: 'Measure',
      addToPlan: 'Add to plan',
      addToPlanAria: 'Confirm {label} and add it to the plan',
      recordOnly: 'Record only',
      recordOnlyAria: 'Confirm {label} on the record only',
      relationAddresses: 'addresses',
      relationTracks: 'tracks',
      relationPrescribes: 'prescribes',
      targetGoal: 'Target a goal',
      targetGoalAria: 'Choose the goal to track {label}',
      goalPickerTitle: 'Track this reading on',
      goalPickerEmpty: 'No goals on the plan yet.',
      replaces: 'replaces {label}',
      noGoalRecordOnly: 'No goal, record only',
      historyVisits: '{count} visits',
    },
    conditions: {
      title: 'Conditions',
      onset: 'Onset: {date}',
      recorded: 'Recorded: {date}',
      denial: 'Denial',
      editCondition: 'Edit Condition',
    },
    goals: {
      title: 'Goals',
      editGoal: 'Edit Goal',
      addGoal: 'Add Goal',
      deleteGoal: 'Delete Goal',
      deleteConfirmTitle: 'Delete this goal?',
      deleteConfirmBody:
        'This removes the goal from the care plan and the patient’s record. This can’t be undone.',
      textPlaceholder: 'e.g. Restore cervical flexion to 50 degrees',
      textAriaLabel: 'Goal text',
      targetDate: 'Target date',
      targetDateAriaLabel: 'Goal target date',
      markDone: 'Mark goal as done',
      markPrimary: 'Mark goal as primary goal',
      primaryHint: 'Listed first, only one per plan',
      primary: 'Primary',
      target: 'Target',
      status: 'Status',
      done: 'Done',
    },
    measures: {
      title: 'Objective Measures',
      editTitle: 'Edit Objective Measure',
      addTitle: 'Add Objective Measure',
      addMeasure: 'Add Measure',
      deleteMeasure: 'Delete Measure',
      deleteConfirmTitle: 'Delete this measure?',
      deleteConfirmBody:
        'This removes the measure from the care plan and the patient’s record. This can’t be undone.',
      namePlaceholder: 'Measure name',
      target: 'Target',
      targetPlaceholder: 'e.g. 80°',
      targetAriaLabel: 'Measure target',
      targetValue: 'Target {target}',
      thisVisitAriaLabel: '{label} this visit',
      trendAriaLabel: 'Measure trend',
      lastCaptured: 'Last: {value} on {date}',
      lastCapturedNoDate: 'Last: {value}',
    },
    actionItems: {
      title: 'Patient To-dos',
      editTitle: 'Edit Patient To-do',
      addActionItem: 'Add Patient To-do',
      deleteActionItem: 'Delete Patient To-do',
      deleteConfirmTitle: 'Delete this patient to-do?',
      deleteConfirmBody:
        'This removes the to-do from the care plan and the patient’s record. This can’t be undone.',
      textPlaceholder: 'e.g. Chin tucks — 3×10 daily',
      textAriaLabel: 'Patient to-do text',
      empty: 'No patient to-dos yet — exercises land here once prescribed.',
    },
    checklist: {
      title: 'Care plan checklist',
      tierComplete: 'Complete',
      tierGood: 'Good',
      tierFair: 'Fair',
      tierWeak: 'Weak',
      progress: '{done} of {total}',
      setReassessFirst: 'Set reassessment timing in Schedule first',
      confirmCarePlan: 'Confirm Care Plan',
      confirmHint:
        'Confirm to activate this plan and carry it over to future visits.',
    },
    phase: {
      title: 'Phase',
      planStarted: 'Plan started {date}',
      setPhaseAriaLabel: 'Set phase {number}',
      editAriaLabel: 'Edit phase',
    },
    schedule: {
      title: 'Schedule',
      editAriaLabel: 'Edit schedule',
      visitsCount: '{count} visits',
      ongoing: 'Ongoing',
      defaultLabel: 'default',
      earlierVisits: 'Earlier visits',
      laterVisits: 'Later visits',
      reassessFlag: '⚑ Reassess',
      visitStatusTitle: 'Visit {number} · {status}',
      statusThisVisit: 'this visit',
      statusCompleted: 'completed',
      statusUpcoming: 'upcoming',
      cadence: 'Cadence',
      cadenceForever: '{cadence} forever',
      cadenceFor: '{cadence} for {duration} {unit}',
      cadenceEvery: '{frequency}× every {period} {unit}',
      unitDay: 'day',
      unitDays: 'days',
      unitWk: 'wk',
      unitWks: 'wks',
      unitMo: 'mo',
      segmentFrequencyAria: 'Segment {number} frequency',
      segmentPeriodUnitAria: 'Segment {number} period unit',
      segmentDurationAria: 'Segment {number} duration',
      segmentDurationUnitAria: 'Segment {number} duration unit',
      removeSegmentAria: 'Remove segment {number}',
      perWeek: 'per week',
      perMonth: 'per month',
      for: 'for',
      weeks: 'weeks',
      months: 'months',
      days: 'days',
      forever: 'forever',
      addCadence: 'Add Cadence',
      reassessAfter: 'Reassess after',
      reassessUnitAria: 'Reassess unit',
      visitsOption: 'visits',
      reassessRequired: 'Set a reassessment before saving.',
      proposedChange: 'This visit proposes a different schedule',
      fromTranscript: 'From this visit',
    },
    recap: {
      title: 'Pre-visit recap',
      goals: 'Goals',
      noGoals: 'No goals on the plan yet.',
      targetDate: 'Target {date}',
      doneLastVisit: 'Done last visit',
      noExercises: 'No exercises from the last visit.',
      addressThisVisit: 'Address this visit',
      nothingDue: 'Nothing flagged as due.',
      lastValue: 'last {value}',
    },
    callout: {
      carePlan: 'Care Plan',
      dismiss: 'Dismiss',
      draftedPrefix: 'Eluve drafted a',
      draftedSuffix: 'for this visit —',
      reviewAndTailor: 'Review & tailor',
      preparingPrefix: 'Eluve is preparing a',
      preparingSuffix: 'for this visit…',
      none: 'No care plan was recommended for this visit.',
      failed: "Eluve couldn't prepare a care plan for this visit.",
      notRequested: 'No care plan has been generated for this visit.',
      generateHint: 'Match this visit against your Care Library',
      alreadyExists: 'This visit already has a care plan',
      notCompleted: 'Only a completed visit can generate a care plan',
      noMatch: 'No care plan template matched this visit.',
      tryAgain: 'Try again',
      generate: 'Generate care plan',
    },
    chat: {
      thinking: 'Thinking…',
      stop: 'Stop',
      send: 'Send',
      draftUpdated: 'Draft updated',
      valid: 'valid',
      issueCountOne: '{count} issue',
      issueCountMany: '{count} issues',
      validatingWithMedplum: 'Validating with Medplum',
    },
    counts: {
      phaseOne: '{count} phase',
      phaseMany: '{count} phases',
      activityOne: '{count} activity',
      activityMany: '{count} activities',
      eventOne: '{count} event',
      eventMany: '{count} events',
      bandOne: '{count} band',
      bandMany: '{count} bands',
    },
    tab: {
      backToNote: 'Back to Note',
      acceptPlan: 'Accept plan',
      accepted: 'Accepted',
      recommended: 'Recommended from this encounter',
      percentMatch: '{pct}% match',
      phased: 'Phased',
      weeksCount: '{count} weeks',
      startsOn: 'Starts {date}',
      templateLoadFailed: "We couldn't load the recommended plan's template.",
      carePlanAccepted: 'Care plan accepted',
      acceptFailed: 'Accept failed: {error}',
    },
    builder: {
      title: 'Plan Builder',
      subtitle: 'Describe a care journey — save it as a reusable template',
      medplumUnavailable: 'Medplum is not available — cannot save',
      planSaved: 'Plan saved',
      saveFailed: 'Save failed: {error}',
      emptyTitle: 'Describe a care journey',
      emptyBody:
        "The Plan Builder classifies it into a Plan Shape, drafts the structure, and iterates with you. You save when it's right — nothing is stored until then.",
      suggestionPhased:
        'A 4-phase ACL rehab protocol: protect the graft, restore range of motion, rebuild strength, return to sport — each phase gated by criteria before advancing.',
      suggestionEpisodic:
        'Chronic migraine management: a monthly maintenance baseline, with a defined same-week response when an acute flare hits and an escalation path for medication overuse.',
      suggestionContinuous:
        'Ongoing type 2 diabetes management: keep HbA1c between 6 and 7.5%, review quarterly, and escalate after two consecutive out-of-range readings.',
      attachFiles: 'Attach files',
      composerPlaceholder:
        'Describe the care journey… (Enter to send, Shift+Enter for newline)',
      draftingPlan: 'Drafting plan',
      resolvingIssuesOne: 'Resolving {count} validation issue',
      resolvingIssuesMany: 'Resolving {count} validation issues',
      toolSearchingRecords: 'Searching clinical records',
      toolSearchedRecords: 'Searched clinical records',
      toolSearchFailed: 'Clinical records search failed',
      toolReadingRecord: 'Reading clinical record',
      toolReadRecord: 'Read clinical record',
      toolReadFailed: 'Clinical record read failed',
      toolWorking: 'Working',
      toolDone: 'Done',
      toolFailed: 'Failed',
      attachedImageAlt: 'attached image',
      removeAttachment: 'Remove {name}',
      attachmentFallbackName: 'attachment',
      fileTypeUnsupported:
        '{name}: only JPEG, PNG, GIF, or WebP images, or PDF documents are supported.',
      pdfTooLarge: '{name}: PDFs must be 10MB or smaller.',
      imageTooLarge: '{name}: images must be 5MB or smaller.',
      tooManyAttachments: 'Up to {max} attachments per message.',
      attachmentsTooLarge:
        '{name}: not enough room — attachments must total under 22MB.',
    },
    preview: {
      noDraftYet: 'No draft yet',
      noDraftHint:
        'Describe a care journey in the chat — the living draft renders here as the Plan Builder shapes it.',
      savePublishesPrefix: 'Saving publishes this template (status',
      savePublishesSuffix: ').',
      saving: 'Saving…',
      saveAsPlanDefinition: 'Save as Plan Definition',
      condition: 'Condition',
      shapingPlan: 'The Plan Builder is shaping your plan',
      buildsLive: 'The draft builds here live as it streams.',
      resolvingRedrafting: 'Resolving validation issues — re-drafting…',
      draftingStreaming: 'Drafting — the plan is still streaming in.',
      validatingDraft: 'Validating the draft against Medplum…',
      bannerResolvingOne: 'Resolving {count} validation issue — re-drafting',
      bannerResolvingMany: 'Resolving {count} validation issues — re-drafting',
      bannerDrafting: 'Drafting — the plan builds live as it streams',
      bannerValidating: 'Draft complete — validating against Medplum',
      validFhir: 'Valid FHIR, ready to save.',
      validationFailedWillFix:
        'Validation failed — the Plan Builder will fix and re-emit before this can be saved.',
      previewFhir: 'Preview FHIR',
      previewFhirDescription:
        'The exact transaction Bundle Save will post — the PlanDefinition plus its ActivityDefinitions, compiled deterministically from the current draft.',
      bundleSingle: '· the PlanDefinition Save will create',
      bundleMany: '· the {count} resources Save will create',
    },
    templates: {
      pageTitle: 'Care Plan Templates',
      newPlan: 'New plan',
      allCarePlans: 'All care plans',
      requiresMedplum:
        'Care plan templates require Medplum to be enabled for this organization.',
      countOne: '{count} template',
      countMany: '{count} templates',
      loading: 'Loading…',
      searchPlaceholder: 'Search by name, condition, or status…',
      loadFailed: 'Failed to load care plan templates: {error}',
      detailLoadFailed: 'Failed to load this care plan template: {error}',
      noMatchesPrefix: 'No templates match "',
      noMatchesSuffix: '".',
      emptyState: 'No care plan templates yet.',
      colTemplate: 'Template',
      colConditions: 'Conditions',
      colShape: 'Shape',
      colVersion: 'Version',
      colStatus: 'Status',
      fhirJson: 'FHIR JSON',
      fhirJsonDescription: 'The stored PlanDefinition resource.',
      viewFhir: 'View FHIR',
    },
  },
  smartBlocks: {
    featureFlagged: {
      redirected: 'You have been redirected',
    },
    amendment: {
      updateFailed: 'Failed to update amendment',
      deleteFailed: 'Failed to delete amendment',
      duplicateFailed: 'Failed to duplicate content to amendment',
    },
    appointmentName: {
      updateFailed: 'Failed to update appointment name',
      updateLabel: 'Update appointment name',
    },
    insightFeedback: {
      thanks: 'Thanks for your feedback! This helps us improve Eluve.',
      submitFailed: 'Failed to submit feedback',
    },
    inlineFeedback: {
      sendFailed: 'Failed to send feedback',
      updateFailed: 'Failed to update feedback',
      updateCommentFailed: 'Failed to update feedback comment',
    },
    customInstructions: {
      title: 'Improve Notes',
      description:
        'Customize your notes by providing specific guidelines. Try to be as clear and concise as possible for best results.',
      placeholder: 'Type your instructions here',
      examples: 'Examples',
      appliesToThisNote: 'These changes will only apply to this note.',
      regenerateNote: 'Regenerate Note',
      regenerating: 'Regenerating your summary',
      regenerateFailed: 'Failed to regenerate summary. Please try again',
      chipRemoveParts: 'Remove parts about...',
      chipIncludeDiscussion: 'Include discussion about...',
      chipMoreConcise: 'Make notes more concise...',
      chipMoreDetails: 'Add more details in the plan section...',
    },
    rollback: {
      title: 'Undo Changes',
      tooltip:
        'You can still go back to the previous version by tapping on the undo button.',
      failed: 'Failed to rollback to previous summary',
      undo: 'Undo',
    },
    lifecycleStatus: {
      updated: 'Status updated to "{status}".',
      changedElsewhere:
        "This user's status was just changed elsewhere. Refreshing.",
      updateFailed: 'Status could not be updated.',
      changeStatusAria: 'Change status, currently {status}',
    },
    promptTemplateSelector: {
      noMatches: 'No matching templates found.',
      loadingDefault: 'Loading default template...',
      selectTemplate: 'Select a template',
      searchPlaceholder: 'Search prompt templates...',
    },
    locationSelector: {
      selectLocation: 'Select a location',
      searchPlaceholder: 'Search locations...',
      noLocation: 'No location',
    },
    patientSelector: {
      selectPatient: 'Select Patient',
      searchPlaceholder: 'Search by name, phone, or email',
      clearSelected: 'Clear Selected Patient',
      searching: 'Searching patients...',
      typeToSearch: 'Type at least 2 characters to search',
      patientAdded: 'Patient {name} added',
      addFailed: 'Failed to add new patient',
      createNew: 'Create New',
      dob: 'DOB:',
    },
    microphoneSelector: {
      ariaLabel: 'Audio Source Selector',
      selectMicrophone: 'Select a microphone',
    },
    micSettings: {
      audioSource: 'Audio Source',
    },
    tabNav: {
      more: 'More',
      moreTabsAria: 'More navigation tabs',
      selectTab: 'Select a tab',
      tabs: 'Tabs',
    },
    statusWidget: {
      uploading: 'Uploading',
      offline: 'Offline',
      uploadingOneFile: 'Uploading a file',
      uploadingManyFiles: 'Uploading {count} files',
      keepOpen:
        'Please keep Eluve open and your device connected to the internet until this is done.',
      currentlyOffline: 'You are currently offline',
      offlineHint:
        'If you are in session you can continue recording, but some features like note generation might require a connection.',
    },
    goToPatient: {
      tooltip: 'Go to patient',
    },
    dynamicArtifact: {
      emailSubject: "Follow up from today's session ({date})",
      emailCopied: 'Email body copied to clipboard — paste it into your email',
      sendMailTooltip: 'Send mail to patient',
      addAmendments: 'Add Amendments',
      valueFallback: 'Value',
    },
    templateBuilder: {
      allBlocksMustBeValid: 'All blocks must be valid',
      atLeastOneBlockRequired: 'At least one non-group block is required',
      requiredField: 'Required',
      aboutThisTemplate: 'About This Template',
      export: 'Export',
      exportTemplate: 'Export Template',
      jsonSchemaCopied: 'Copied JSON schema to clipboard',
      copyJsonSchema: 'Copy JSON Schema',
      pasteJsonSchemaPlaceholder: 'Paste JSON schema here',
      nameLabel: 'Name',
      typeLabel: 'Type',
      outputTypePlaceholder: 'Output Type',
      descriptionLabel: 'Description',
      descriptionPlaceholder:
        'Provide a description of what the artifact produced by this template should be.',
      variantNotes: 'Variant Notes',
      variantNotesDescription:
        'Internal notes for this template variant. These are visible to template editors and will not appear in generated content.',
      variantNotesPlaceholder: 'Add notes about this variant...',
      blocks: 'Blocks',
      collapseAll: 'Collapse All',
      expandAll: 'Expand All',
      viewJson: 'View JSON',
      templateJson: 'Template JSON',
      templateJsonCopied: 'Template JSON copied to clipboard.',
      templateJsonCopyFailed: 'Template JSON could not be copied.',
      addBlock: 'Add Block',
      groupNamePlaceholder: 'Group name',
      textBlockNamePlaceholder: 'Text block name',
      checkboxNamePlaceholder: 'Checkbox name',
      expand: 'Expand',
      collapse: 'Collapse',
      removeBlock: 'Remove block',
      confirmRemoveTitle: 'Are you sure?',
      confirmRemoveDescription: 'Removing this block cannot be reversed.',
      remove: 'Remove',
    },
    blockDetails: {
      fillOutBlockWith: 'Fill out this block with',
      ai: 'AI',
      manual: 'Manual',
      aiOptionDescription:
        'AI will fill out this block. User can edit the AI draft.',
      manualOptionDescription:
        'This block will start with the placeholder text. User fills it in.',
      required: 'Required',
      requiredDescription:
        'AI will always try to fill out this block. It should never leave it blank.',
      allowEmail: 'Allow users to email this block',
      allowEmailDescription:
        'If enabled, users will be given controls to send the contents of this block by email.',
      aiInstructions: 'AI Instructions',
      aiInstructionsPlaceholder:
        'Describe what the AI should generate in this block. For example, "Write a short description of the patient\'s chief complaint. Do not use full sentences."',
      checkboxes: 'Checkboxes',
      optionPlaceholder: 'Option {number}',
      addOption: 'Add Option',
      placeholderTextTooltip:
        'When used in Manual Charting mode, this text will be pre-filled in the text box.',
      placeholderText: 'Placeholder Text',
      importedDescription: 'Imported Description',
      importedDescriptionTooltip:
        'This description was imported from the EHR. The imported description will be used as a hint to AI when generating a note. Override it by providing your own description above to be used for AI.',
    },
    blockPicker: {
      groupName: 'Group',
      groupTip: 'Container for other blocks',
      textName: 'Text block',
      textTip: 'Freeform text',
      checkboxName: 'Checkboxes',
      checkboxTip: 'List of checkbox options',
      add: 'Add',
    },
  },
  userSettings: {
    save: 'Save',
    updateProfileFailed: 'Failed to update profile',
    profile: {
      dialogTitle: 'Edit Full Name',
      editFullName: 'Edit full name',
      firstName: 'First Name',
      lastName: 'Last Name',
    },
    language: {
      notesLanguage: 'Notes Language',
      notesLanguageDescription: 'Language used in the generated note.',
      spokenLanguage: 'Spoken Language',
      spokenLanguageDescription: 'Language spoken during your sessions.',
    },
    providerDetails: {
      placeholder:
        'Anything that needs to appear on signed notes: license #, NPI, DEA, etc.',
    },
    soundEffects: {
      updateFailed:
        'Failed to update sound effects settings. Please try again.',
      label: 'Sound Effects',
    },
    appGrants: {
      empty: "You haven't installed any agents yet.",
      granted: 'Granted {date}',
      unknownDate: 'unknown',
      revoke: 'Revoke',
      revoked: 'Revoked {agentId}',
      revokeFailed: 'Revoke failed',
    },
    signature: {
      invalidFileExtension: 'Invalid file extension.',
      uploadFailed: 'We could not upload the file.',
      updateFailed: 'We could not update your signature.',
      alt: 'Signature',
    },
    specialty: {
      updateFailed: 'Specialty could not be updated.',
    },
    templates: {
      updateFailed: 'Failed to update default template',
      loading: 'Loading Templates...',
      select: 'Select Template',
    },
  },
  insightsSettings: {
    customCharts: {
      title: 'Custom Charts',
      description: 'Paste chart data → Generate insights via API',
      placeholder: 'Paste JSON array of chart data',
      invalidSchema: 'Invalid schema',
      invalidJsonSyntax: 'Invalid JSON syntax',
      jsonParseError: 'JSON parse error: {error}',
      processFailed: 'Failed to process JSON',
      validJson: 'Valid JSON - ready to generate',
      generating: 'Generating...',
      generate: 'Generate Insights',
    },
  },
  careLibrary: {
    page: {
      eyebrow: 'Library',
      loadError: 'Something went wrong loading the Care Library.',
      medplumRequired:
        'The Care Library requires Medplum to be enabled for this organization.',
      loadingOfferings: 'Loading offerings…',
    },
    browse: {
      allTypes: 'All types',
      offeringsCountLabel: 'offerings',
      typesCountLabel: 'types',
      searchPlaceholder: 'Search offerings…',
      searchAriaLabel: 'Search offerings',
      allOfferings: 'All offerings',
      typeOfferings: '{type} offerings',
      noMatches: 'No offerings match the current filters.',
    },
    rail: {
      browseByCategory: 'Browse by category',
      primaryFacet: 'primary',
    },
    detail: {
      backToLibrary: 'Back to Care Library',
      loadError: 'Something went wrong loading this offering.',
      loadingOffering: 'Loading offering…',
      notFound: 'That offering could not be found.',
      noClinicalCode: 'No clinical code',
      parameters: 'Parameters',
      parametersNote: '· {type} · prescribed per patient at plan time',
      patientInstructions: 'Patient-facing instructions',
      categoriesTitle: 'Categories',
      belongsToPrefix: 'Belongs to',
      belongsToSuffix: 'categories — an item can sit in several at once.',
      usedInPlansTitle: 'Used in plans',
      usedInPlansEmpty:
        'The plans that reference this offering will be listed here, each with the dose it prescribes, once the catalog is backed by live plan data.',
      reuseByReference:
        'Reuse is by reference. Editing this entry propagates to every plan automatically.',
    },
  },
  microphone: {
    noAudioDetectedCompact: 'Audio not detected.',
    noAudioDetectedFull:
      'Audio not detected. Please select a different audio source to start.',
    permissionsDeniedTitle: 'Microphone permissions denied',
    permissionsDeniedMessage:
      'Please enable microphone permissions in your browser settings to enable audio input.',
    noDevicesTitle: 'Microphone devices required',
    noDevicesMessage:
      'There are no microphone devices connected to your system, please grant microphone permissions or retry.',
    retryPermission: 'Retry Permission',
    permissionsRequiredTitle: 'Microphone permissions required',
    permissionsRequiredMessage:
      'Please grant microphone permissions to enable audio input.',
    requestPermission: 'Request Permission',
  },
};

/**
 * Canonical shape of a translation bundle, derived from {@link enMessages}.
 * Every non-English locale file must conform to this type, so the compiler
 * enforces that every English key has a translation in every other locale.
 */
export type Messages = typeof enMessages;
